// src/pages/CaseAnalyzerPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  abandonCaseAnalyzerJob,
  createCaseAnalyzerJob,
  getCaseAnalyzerUploadUserMessage,
  uploadCaseAnalyzerEvidence,
  startCaseAnalyzerRun,
} from "../utils/caseAnalyzerApiClient";

const MAX_FILE_MB = 200;
const MAX_TOTAL_UPLOAD_MB = 750;
const MAX_TOTAL_PAGES = 1500;
const MAX_FILES_SAFETY = 1000;
const FACTS_SUMMARY_MIN_CHARS = 200;
const CASE_ANALYZER_PAGE_TIERS = [250, 500, 750, 1000, 1250, 1500];
const CASE_ANALYZER_FILE_LIMITS = {
  250: 40,
  500: 50,
  750: 60,
  1000: 70,
  1250: 80,
  1500: 90,
};
const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_TOTAL_BYTES = MAX_TOTAL_UPLOAD_MB * 1024 * 1024;
const CASE_ANALYZER_LOCKED_STATUSES = new Set([
  "queued",
  "dispatched",
  "worker_received",
  "verifying_package",
  "ingesting",
  "extracting",
  "chunking",
  "summarizing_files",
  "mapping_evidence",
  "analyzing",
  "analyzing_case",
  "building_report",
  "saving_report",
  "complete",
  "completed",
  "report_ready",
  "failed",
  "error",
]);

function isLockedStatus(status) {
  return CASE_ANALYZER_LOCKED_STATUSES.has(String(status || "").trim().toLowerCase());
}

function isAlreadyLockedAnalyzeError(err) {
  const status = Number(err?.status || err?.statusCode || 0);
  const message = String(err?.message || err?.response?.error || err?.response?.message || "").toLowerCase();
  return (status === 400 || status === 409) && (
    message.includes("already locked") ||
    message.includes("locked for analysis") ||
    message.includes("already queued") ||
    message.includes("already processing") ||
    message.includes("processing") ||
    message.includes("queued") ||
    message.includes("analysis")
  );
}


const CASE_TYPES = [
  "Not Sure / Help Me Decide",
  "Contract / Commercial Dispute",
  "Employment",
  "Personal Injury",
  "Landlord / Tenant",
  "Real Estate",
  "Business Partner / Owner Dispute",
  "Consumer / Privacy",
  "Debt / Collection",
  "Bankruptcy",
  "Insurance",
  "Family Law",
  "Immigration",
  "Criminal Defense",
  "Estate / Probate",
  "Tax",
  "Administrative / Government",
  "Other Civil Matter",
];

const SUBTYPES_BY_CASE = {
  "Not Sure / Help Me Decide": ["Not Sure", "Mixed issues", "Help me identify the case type"],
  "Contract / Commercial Dispute": ["Breach of Contract", "Nonpayment", "Service Dispute", "Sales / Goods Dispute", "Vendor / Customer Dispute", "Commercial Lease", "Licensing / IP Contract", "SaaS / Subscription", "Refund / Chargeback", "Other Contract Issue"],
  Employment: ["Wrongful Termination", "Wage / Hour", "Discrimination", "Harassment", "Retaliation", "Severance / Negotiation", "Misclassification", "Non-Compete / Trade Secrets", "Workplace Accommodation", "Other Employment Issue"],
  "Personal Injury": ["Auto Accident", "Premises Liability", "Medical Malpractice", "Product Liability", "Workplace Injury", "Wrongful Death", "Dog Bite / Animal Injury", "Other Injury"],
  "Landlord / Tenant": ["Security Deposit", "Habitability / Repairs", "Eviction / Unlawful Detainer", "Rent Increase / Rent Control", "Lease Violation", "Fair Housing", "Lockout / Utilities", "Other Landlord / Tenant Issue"],
  "Real Estate": ["Purchase / Sale Dispute", "Disclosure Issue", "Boundary / Easement", "HOA / Condo", "Construction Defect", "Title / Escrow", "Partition / Co-owner Dispute", "Other Real Estate Issue"],
  "Business Partner / Owner Dispute": ["Shareholder / Member Dispute", "Fiduciary Duty", "Fraud / Misrepresentation", "Buyout / Dissolution", "Books and Records", "Operating Agreement Dispute", "Unauthorized Spending", "Owner Lockout", "Other Owner Dispute"],
  "Consumer / Privacy": ["Defective Product / Service", "False Advertising", "Privacy / Data Use", "Account Suspension", "Subscription / Cancellation", "Warranty", "Fraud / Scam", "Other Consumer Issue"],
  "Debt / Collection": ["Debt Collection Defense", "Collection Lawsuit", "Credit Reporting", "Harassing Calls / Notices", "Payment Plan / Settlement", "Judgment Collection", "Other Debt Issue"],
  Bankruptcy: ["Chapter 7 Bankruptcy", "Chapter 13 Bankruptcy", "Chapter 11 / Business Bankruptcy", "Creditor Claim in Bankruptcy", "Automatic Stay Violation", "Discharge or Dischargeability Dispute", "Bankruptcy Filing Evaluation", "Reaffirmation / Secured Debt Issue", "Exemptions / Property Protection", "Dismissal or Conversion", "Other Bankruptcy Issue"],
  Insurance: ["Claim Denial", "Low Settlement Offer", "Coverage Dispute", "Delay / Bad Faith", "Property Claim", "Auto Claim", "Health / Disability Claim", "Other Insurance Issue"],
  "Family Law": ["Divorce / Separation", "Custody / Visitation", "Child Support", "Spousal Support", "Property Division", "Domestic Violence / Protective Order", "Modification / Enforcement", "Other Family Issue"],
  Immigration: ["Family Petition", "Work Visa", "Removal / Deportation", "Asylum / Humanitarian", "Naturalization", "Adjustment of Status", "Request for Evidence", "Other Immigration Issue"],
  "Criminal Defense": ["DUI / DWI", "Theft / Fraud", "Assault / Battery", "Drug Charge", "Domestic Violence", "Traffic / Infraction", "Probation / Violation", "Expungement / Record Relief", "Other Criminal Issue"],
  "Estate / Probate": ["Will / Trust Contest", "Probate Administration", "Executor / Trustee Dispute", "Beneficiary Dispute", "Elder Financial Abuse", "Guardianship / Conservatorship", "Accounting / Distribution", "Other Probate Issue"],
  Tax: ["IRS / State Notice", "Audit / Examination", "Penalty / Interest", "Collection / Levy / Lien", "Payroll Tax", "Sales Tax", "Tax Court / Appeal", "Other Tax Issue"],
  "Administrative / Government": ["License / Permit", "Benefits Denial", "Citation / Fine", "Agency Appeal", "Public Records", "School / Education", "Professional Discipline", "Other Administrative Issue"],
  "Other Civil Matter": ["General Civil Claim", "Harassment / Restraining Order", "Defamation", "Neighbor Dispute", "Civil Rights", "Fraud / Misrepresentation", "Settlement Evaluation", "Other"],
};

const USER_ROLES = [
  "I am a potential claimant / no case filed yet",
  "I am the plaintiff / claimant",
  "I am the petitioner / applicant",
  "I am the defendant / respondent",
  "I am the appellant",
  "I am responding to an appeal",
  "I am a debtor",
  "I am a creditor",
  "I am an accused or charged person",
  "I am a beneficiary / heir",
  "I represent a business or organization",
  "I am evaluating a settlement",
  "I am not sure",
];
const PARTY_TYPES = [
  "Individual",
  "Business / Organization",
  "Employer",
  "Employee",
  "Landlord",
  "Tenant",
  "Insurance Company",
  "Creditor / Debt Collector",
  "Debtor",
  "Government / Agency",
  "Healthcare Provider",
  "Property Owner / Manager",
  "Trustee / Executor / Fiduciary",
  "Unknown",
];
const FORUMS = ["Pre-lawsuit / no filing yet", "Small Claims", "State Court", "Federal Court", "Arbitration", "Agency / Administrative", "Mediation / Settlement", "Unknown"];
const CASE_STAGES = ["Just evaluating", "Demand letter stage", "Settlement negotiation", "Complaint / petition filed", "Answer / response filed", "Discovery", "Motion stage", "Mediation scheduled", "Trial / hearing scheduled", "Judgment / order entered", "Appeal", "Unknown"];
const YES_NO_UNKNOWN = ["Yes", "No", "Unknown"];
const SUPPORTS_SIDE = ["My position", "Other side", "Mixed", "Not sure"];
const IMPORTANCE_LEVELS = ["High", "Medium", "Low", "Not sure"];
const EVIDENCE_QUALITY_LEVELS = ["Strong", "Moderate", "Weak", "Not sure"];

const CORRESPONDENCE_TYPES = ["Emails", "Text messages", "Letters", "Demand letter sent", "Demand letter received", "Settlement offer", "Notice of breach / default", "Notice to quit / eviction notice", "Termination letter", "HR complaint", "Insurance communications", "Court / agency notices", "Phone calls only", "No correspondence", "Not sure"];

const RELIEF_TYPES = [
  "Money owed",
  "Out-of-pocket costs",
  "Lost income / wages",
  "Property damage",
  "Medical bills",
  "Future costs",
  "Emotional distress",
  "Statutory penalties",
  "Attorney fees",
  "Injunction / stop conduct",
  "Specific performance / make them do something",
  "Dismissal / defense",
  "Repair / replacement",
  "Refund / cancellation",
  "Other",
  "Not sure",
];

const UNIVERSAL_EVIDENCE_BUNDLES = [
  { id: "core_case_documents", label: "Core case documents", hint: "Use this for the main papers that explain or start the dispute, such as a complaint, petition, claim, answer, response, case summary, or demand packet." },
  { id: "court_filings_orders", label: "Court filings / orders / docket records", hint: "Use this for formal court records, including motions, orders, judgments, notices of hearing, docket sheets, pleadings, or filed case documents." },
  { id: "contracts_agreements_terms", label: "Contracts / agreements / written terms", hint: "Use this for written obligations, including contracts, leases, policies, terms, handbooks, addenda, statements of work, order forms, or amendments." },
  { id: "communications_messages", label: "Communications / messages / correspondence", hint: "Use this for ordinary back-and-forth communications, including emails, texts, letters, chats, admissions, denials, promises, explanations, or negotiations." },
  { id: "attorney_correspondence", label: "Attorney correspondence / legal letters", hint: "Use this for attorney-to-attorney letters, legal position letters, meet-and-confer letters, representation letters, litigation hold letters, or counsel communications." },
  { id: "notices_demands_warnings", label: "Notices / demands / warnings", hint: "Use this for formal notices that may trigger rights or deadlines, including demand letters, default notices, cure notices, warnings, termination letters, or violation notices." },
  { id: "payments_invoices_receipts", label: "Payments / invoices / receipts", hint: "Use this for transaction proof, including invoices, receipts, payment records, account ledgers, bank proof, refunds, chargebacks, balances, or payment history." },
  { id: "financial_records", label: "Financial / accounting records", hint: "Use this for financial proof, including bank statements, profit and loss reports, balance sheets, accounting books, tax support, distributions, or account records." },
  { id: "damages_losses_costs", label: "Damages / losses / costs", hint: "Use this for proof of harm or claimed losses, including repair costs, lost income, replacement costs, out-of-pocket expenses, damage calculations, or future costs." },
  { id: "medical_records", label: "Medical records / treatment / bills", hint: "Use this for medical proof, including diagnoses, treatment notes, bills, prescriptions, therapy records, discharge summaries, medical opinions, or future care records." },
  { id: "photos_screenshots_visuals", label: "Photos / screenshots / visual proof", hint: "Use this for visual evidence, including photos, screenshots, app screens, damaged property, injuries, unsafe conditions, product condition, or still images from video." },
  { id: "professional_expert_reports", label: "Professional / expert reports", hint: "Use this for professional opinions or technical records, including inspections, appraisals, expert letters, contractor reports, repair reports, or specialist findings." },
  { id: "official_government_agency_records", label: "Official / government / agency records", hint: "Use this for official records, including agency notices, citations, permits, police reports, inspection reports, public records, government decisions, or administrative records." },
  { id: "witness_statements_declarations", label: "Witness statements / declarations", hint: "Use this for statements from people with firsthand knowledge, including affidavits, declarations, signed summaries, witness letters, or witness notes." },
  { id: "timeline_logs_notes", label: "Timeline / logs / notes", hint: "Use this for chronology evidence, including incident logs, call logs, calendar entries, journal notes, internal notes, event lists, or written timelines." },
  { id: "settlement_negotiation_records", label: "Settlement / negotiation records", hint: "Use this for resolution-related documents, including settlement offers, mediation communications, release drafts, payment plans, negotiation summaries, or proposed agreements." },
  { id: "discovery_records", label: "Discovery / exchanged evidence", hint: "Use this for litigation discovery, including interrogatories, document requests, subpoenas, responses, produced documents, exhibit lists, or exchanged evidence indexes." },
  { id: "deposition_hearing_transcripts", label: "Transcripts / testimony records", hint: "Use this for sworn testimony or hearing records, including deposition transcripts, hearing transcripts, trial testimony, witness examination excerpts, or official testimony records." },
  { id: "service_deadline_proof", label: "Service / deadline / procedural proof", hint: "Use this for procedural proof, including proof of service, filing receipts, mailing proof, deadline notices, jurisdiction documents, arbitration prerequisites, or pre-suit step records." },
  { id: "ownership_title_asset_records", label: "Ownership / title / asset records", hint: "Use this for ownership or asset proof, including deeds, titles, ownership certificates, asset lists, inventory records, escrow papers, title reports, or transfer records." },
  { id: "property_condition_records", label: "Property condition / inspection records", hint: "Use this for condition evidence, including inspections, move-in or move-out records, defect reports, repair history, maintenance records, property photos, or condition summaries." },
  { id: "business_governance_records", label: "Business governance / ownership records", hint: "Use this for business control and ownership proof, including operating agreements, bylaws, shareholder records, member records, cap tables, resolutions, or governance notices." },
  { id: "tax_records_notices", label: "Tax records / notices / filings", hint: "Use this for tax-related proof, including IRS or state notices, tax returns, transcripts, assessments, audit letters, payroll tax records, sales tax records, or tax correspondence." },
  { id: "insurance_policy_claim_records", label: "Insurance policy / claim records", hint: "Use this for insurance proof, including policies, declaration pages, endorsements, claim submissions, adjuster letters, denial letters, estimates, or claim file records." },
  { id: "employment_personnel_records", label: "Personnel / workplace records", hint: "Use this for workplace records, including offer letters, HR files, discipline, termination records, performance reviews, workplace complaints, or internal employment records." },
  { id: "payroll_time_wage_records", label: "Payroll / time / wage records", hint: "Use this for compensation and time proof, including paystubs, timesheets, schedules, commission records, overtime records, reimbursements, deductions, or wage calculations." },
  { id: "education_school_records", label: "Education / school records", hint: "Use this for school-related records, including attendance, discipline, accommodations, grades, school notices, education plans, incident reports, or administrative school records." },
  { id: "law_enforcement_incident_records", label: "Law enforcement / incident records", hint: "Use this for incident and law-enforcement records, including police reports, citations, tickets, incident reports, bodycam logs, officer notes, or accident exchange records." },
  { id: "technical_digital_records", label: "Technical / digital / system records", hint: "Use this for digital proof, including app logs, system logs, metadata, account logs, audit trails, device records, access logs, or platform activity records." },
  { id: "privacy_data_security_records", label: "Privacy / data / security records", hint: "Use this for privacy or data evidence, including data requests, breach notices, consent screens, access logs, privacy notices, account-security alerts, or data export records." },
  { id: "repair_estimate_service_records", label: "Repair / estimate / service records", hint: "Use this for service or repair proof, including repair estimates, maintenance logs, service invoices, contractor records, work orders, diagnostic reports, or replacement estimates." },
  { id: "inventory_product_records", label: "Inventory / product / goods records", hint: "Use this for goods or product proof, including product records, serial numbers, inventory lists, product specifications, inspection records, warranty records, or condition reports." },
  { id: "shipping_delivery_records", label: "Shipping / delivery / fulfillment records", hint: "Use this for delivery proof, including tracking records, delivery confirmations, packing slips, bills of lading, shipment notices, fulfillment records, or receipt confirmations." },
  { id: "compliance_policy_procedure_records", label: "Compliance / policy / procedure records", hint: "Use this for rules or required processes, including compliance files, safety standards, internal procedures, checklists, training records, policy acknowledgments, or audit records." },
  { id: "benefits_application_records", label: "Applications / benefits / permit records", hint: "Use this for application-based records, including benefit applications, permit files, license applications, renewals, approvals, denials, supporting documents, or application receipts." },
  { id: "family_household_records", label: "Family / household / relationship records", hint: "Use this for relationship or household proof, including caregiving records, household records, relationship evidence, support records, family schedules, or child-related records." },
  { id: "trust_fiduciary_beneficiary_records", label: "Trust / fiduciary / beneficiary records", hint: "Use this for fiduciary or beneficiary proof, including wills, trusts, accountings, distributions, beneficiary forms, trustee or executor letters, powers of attorney, or guardianship authority records." },
  { id: "recording_transcripts_summaries", label: "Recording transcripts / summaries / still captures", hint: "Use this for readable material from recordings, including audio transcripts, voicemail transcripts, video summaries, screenshots from video, bodycam transcript excerpts, or surveillance summaries." },
  { id: "consent_authorization_records", label: "Consent / authorization / approval records", hint: "Use this for permission-based documents, including signed approvals, consent forms, authorizations, waivers, releases, account permissions, HIPAA releases, or approval records." },
  { id: "other_evidence", label: "Other evidence / not sure", hint: "Use this only when none of the listed evidence groups fit. Add a short note explaining what the file is and why it may matter." },
];



function useIsSmallScreen(breakpoint = 1024) {
  const getInitial = () => (typeof window !== "undefined" ? window.innerWidth < breakpoint : false);
  const [isSmall, setIsSmall] = useState(getInitial());
  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isSmall;
}

function makeJobName(form) {
  const type = form.case_type && !form.case_type.startsWith("Not Sure") ? form.case_type : "Case Analyzer";
  const place = form.county || form.city || form.state || "";
  const date = new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return [type, place, date].filter(Boolean).join(" — ");
}

function normalizeNumber(value) {
  const n = Number(String(value || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function formatDuration(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function formatUploadSpeed(bytesPerSecond) {
  const value = Math.max(0, Number(bytesPerSecond || 0));
  if (!value) return "Calculating speed…";
  return `${formatBytes(value)}/s`;
}

function uploadPhaseMessage(event = {}) {
  const fileNumber = Number(event.fileNumber || event.fileIndex || 0);
  const totalFiles = Number(event.totalFiles || 0);
  const fileName = String(event.fileName || "").trim();
  const fileLabel = fileNumber && totalFiles
    ? `file ${fileNumber.toLocaleString()} of ${totalFiles.toLocaleString()}`
    : "your evidence";
  const namedFile = fileName ? `: ${fileName}` : "";

  if (event.type === "batch_start") {
    return `Preparing ${totalFiles.toLocaleString()} evidence file${totalFiles === 1 ? "" : "s"}…`;
  }
  if (event.phase === "requesting_upload_url") return `Preparing ${fileLabel}${namedFile}`;
  if (event.phase === "uploading_file" || event.type === "upload_progress") return `Uploading ${fileLabel}${namedFile}`;
  if (event.phase === "confirming_upload") return `Confirming ${fileLabel}${namedFile}`;
  if (event.type === "file_complete") return `Uploaded ${fileLabel}${namedFile}`;
  if (event.type === "batch_complete") return "All evidence files uploaded successfully.";
  return "Preparing your secure upload…";
}

function makeBundleId(prefix = "bundle") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getFileExtension(fileName = "") {
  const name = String(fileName || "").toLowerCase();
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1) : "";
}

function isPdfFile(file) {
  const ext = getFileExtension(file?.name);
  const type = String(file?.type || "").toLowerCase();
  return ext === "pdf" || type === "application/pdf";
}

function isImageFile(file) {
  const ext = getFileExtension(file?.name);
  const type = String(file?.type || "").toLowerCase();
  return type.startsWith("image/") || ["png", "jpg", "jpeg", "webp"].includes(ext);
}

function isTextFile(file) {
  const ext = getFileExtension(file?.name);
  const type = String(file?.type || "").toLowerCase();
  return type.startsWith("text/") || ["txt", "csv"].includes(ext);
}

async function estimatePdfPageCount(file) {
  const buffer = await file.arrayBuffer();
  const text = new TextDecoder("latin1").decode(buffer);
  const matches = text.match(/\/Type\s*\/Page\b/g);
  return Array.isArray(matches) ? matches.length : 0;
}

async function estimateTextPageCount(file) {
  const text = await file.text();
  const charsPerPage = 3000;
  return Math.max(1, Math.ceil((text || "").length / charsPerPage));
}

async function getFrontendPageInfo(file) {
  if (!file) {
    return {
      page_count: 0,
      pageCount: 0,
      page_count_status: "unknown",
      pageCountStatus: "unknown",
      page_count_source: "missing_file",
      pageCountSource: "missing_file",
    };
  }

  try {
    if (isPdfFile(file)) {
      const pages = await estimatePdfPageCount(file);
      if (pages > 0) {
        return {
          page_count: pages,
          pageCount: pages,
          page_count_status: "provided",
          pageCountStatus: "provided",
          page_count_source: "frontend_pdf_soft_count",
          pageCountSource: "frontend_pdf_soft_count",
        };
      }
      return {
        page_count: 0,
        pageCount: 0,
        page_count_status: "unknown",
        pageCountStatus: "unknown",
        page_count_source: "frontend_pdf_count_failed",
        pageCountSource: "frontend_pdf_count_failed",
      };
    }

    if (isImageFile(file)) {
      return {
        page_count: 1,
        pageCount: 1,
        page_count_status: "provided",
        pageCountStatus: "provided",
        page_count_source: "frontend_image_one_page",
        pageCountSource: "frontend_image_one_page",
      };
    }

    if (isTextFile(file)) {
      const pages = await estimateTextPageCount(file);
      return {
        page_count: pages,
        pageCount: pages,
        page_count_status: "estimated",
        pageCountStatus: "estimated",
        page_count_source: "frontend_text_estimate",
        pageCountSource: "frontend_text_estimate",
      };
    }

    return {
      page_count: 0,
      pageCount: 0,
      page_count_status: "unknown",
      pageCountStatus: "unknown",
      page_count_source: "frontend_not_supported_for_page_count",
      pageCountSource: "frontend_not_supported_for_page_count",
    };
  } catch (err) {
    console.warn("Case Analyzer page count check failed:", err);
    return {
      page_count: 0,
      pageCount: 0,
      page_count_status: "unknown",
      pageCountStatus: "unknown",
      page_count_source: "frontend_page_count_error",
      pageCountSource: "frontend_page_count_error",
    };
  }
}

function normalizePageCountInput(value) {
  const n = Number(String(value ?? "").replace(/[^0-9]/g, ""));
  if (!Number.isFinite(n) || n < 1) return 0;
  return Math.trunc(n);
}

function getItemPageCountStatus(item) {
  return item?.page_count_status || item?.pageCountStatus || "unknown";
}

function getItemPageCountSource(item) {
  return item?.page_count_source || item?.pageCountSource || "";
}

function itemNeedsManualPageCount(item) {
  const status = String(getItemPageCountStatus(item) || "").toLowerCase();
  const source = String(getItemPageCountSource(item) || "").toLowerCase();
  return Boolean(
    item?.manual_page_count_required ||
    item?.manualPageCountRequired ||
    status === "manual_required" ||
    source === "manual_user_entry_pending" ||
    (getDetectedItemPageCount(item) <= 0 && (status === "unknown" || source.includes("failed") || source.includes("not_supported") || source.includes("error")))
  );
}

function getDetectedItemPageCount(item) {
  const needsManual = Boolean(item?.manual_page_count_required || item?.manualPageCountRequired);
  if (needsManual) return 0;
  return Number(item?.detected_page_count || item?.detectedPageCount || item?.page_count || item?.pageCount || 0) || 0;
}

function getUserEnteredItemPageCount(item) {
  if (!itemNeedsManualPageCount(item)) return 0;
  return normalizePageCountInput(item?.manual_page_count || item?.manualPageCount || item?.page_count || item?.pageCount || 0);
}

function getItemPageCount(item) {
  if (itemNeedsManualPageCount(item)) return getUserEnteredItemPageCount(item);
  return getDetectedItemPageCount(item);
}

function getItemPageCountDisplay(item) {
  const pages = getItemPageCount(item);
  if (pages > 0) {
    const source = itemNeedsManualPageCount(item) ? "user-entered" : "detected";
    return `${pages.toLocaleString()} page${pages === 1 ? "" : "s"} (${source})`;
  }
  if (itemNeedsManualPageCount(item)) return "Page count required";
  return "Page count unknown";
}

function readPackageRemaining(pkg) {
  if (typeof pkg === "number" && Number.isFinite(pkg)) return Math.max(0, pkg);
  if (!pkg || typeof pkg !== "object") return 0;

  const candidates = [
    pkg.remaining_reports,
    pkg.remaining_report,
    pkg.remaining_docs,
    pkg.remaining_documents,
    pkg.remaining,
    pkg.count,
    pkg.qty,
    pkg.quantity,
    pkg.reports,
    pkg.docs,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.trunc(n));
  }
  return 0;
}

function readPackageTier(pkg, fallbackKey = "") {
  if (pkg && typeof pkg === "object") {
    const candidates = [pkg.page_tier, pkg.pageTier, pkg.max_pages, pkg.maxPages, pkg.pages, pkg.tier];
    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return Math.trunc(n);
    }
  }

  const match = String(fallbackKey || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function normalizeCaseAnalyzerPackages(bucket) {
  const counts = new Map();
  const raw = bucket && typeof bucket === "object" ? bucket.case_analyzer_page_packages : null;

  if (Array.isArray(raw)) {
    raw.forEach((pkg) => {
      const tier = readPackageTier(pkg);
      if (!tier) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(pkg));
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const tier = readPackageTier(value, key);
      if (!tier) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(value));
    });
  }

  // Compatibility with the monthly bucket while routes are being upgraded.
  const fallbackTier = readPackageTier({ page_tier: bucket?.case_analyzer_page_tier });
  const fallbackRemaining = readPackageRemaining({ remaining_reports: bucket?.case_analyzer_remaining_reports });
  if (fallbackTier > 0 && fallbackRemaining > 0) {
    counts.set(fallbackTier, Math.max(counts.get(fallbackTier) || 0, fallbackRemaining));
  }

  return CASE_ANALYZER_PAGE_TIERS.map((tier) => ({
    page_tier: tier,
    remaining: Math.max(0, Number(counts.get(tier) || 0)),
  }));
}

function mergeCaseAnalyzerPackages(monthlyRows, addonRows) {
  return CASE_ANALYZER_PAGE_TIERS.map((tier) => {
    const monthlyRemaining = Number((monthlyRows || []).find((row) => row.page_tier === tier)?.remaining || 0);
    const addonRemaining = Number((addonRows || []).find((row) => row.page_tier === tier)?.remaining || 0);
    return {
      page_tier: tier,
      monthly_remaining: Math.max(0, monthlyRemaining),
      addon_remaining: Math.max(0, addonRemaining),
      remaining: Math.max(0, monthlyRemaining) + Math.max(0, addonRemaining),
    };
  });
}

function getCaseAnalyzerFileLimit(pageTier) {
  const tier = Number(pageTier || 0);
  return Math.max(0, Number(CASE_ANALYZER_FILE_LIMITS[tier] || 0));
}

function getLowestAvailablePackage(rows) {
  return [...(rows || [])]
    .filter((row) => Number(row?.remaining || 0) > 0)
    .sort((a, b) => Number(a?.page_tier || 0) - Number(b?.page_tier || 0))[0] || null;
}

function getSmallestFittingAvailablePackage(rows, knownPages, fileCount) {
  const pages = Math.max(0, Number(knownPages || 0));
  const files = Math.max(0, Number(fileCount || 0));

  return [...(rows || [])]
    .filter((row) => {
      const tier = Number(row?.page_tier || 0);
      const fileLimit = getCaseAnalyzerFileLimit(tier);
      return Number(row?.remaining || 0) > 0 && tier >= pages && fileLimit >= files;
    })
    .sort((a, b) => Number(a?.page_tier || 0) - Number(b?.page_tier || 0))[0] || null;
}

function getHighestCaseAnalyzerPackageTier(rows) {
  const activeRows = (rows || []).filter((row) => Number(row.remaining || 0) > 0);
  if (!activeRows.length) return 0;
  return Math.max(...activeRows.map((row) => Number(row.page_tier || 0)));
}

function formatPageLimit(tier) {
  const n = Number(tier || 0);
  return n > 0 ? `${n.toLocaleString()} pages` : "No package";
}

function buildSelectedPackageLimitError({
  selectedTier,
  selectedFileLimit,
  knownPages,
  fileCount,
  availablePackages,
}) {
  const tier = Math.max(0, Number(selectedTier || 0));
  const fileLimit = Math.max(0, Number(selectedFileLimit || 0));
  const pages = Math.max(0, Number(knownPages || 0));
  const files = Math.max(0, Number(fileCount || 0));

  if (!tier || !fileLimit) {
    return "Please select one of your available Case Analyzer packages.";
  }

  const overPages = pages > tier;
  const overFiles = files > fileLimit;
  if (!overPages && !overFiles) return "";

  const higherAvailable = (availablePackages || []).some((row) => {
    const higherTier = Number(row?.page_tier || 0);
    return Number(row?.remaining || 0) > 0 && higherTier > tier;
  });
  const resolution = higherAvailable
    ? "Please remove lower-priority files or select an available higher package."
    : "Please remove lower-priority files.";

  if (overPages && overFiles) {
    return `Your selected ${tier.toLocaleString()}-page package allows up to ${tier.toLocaleString()} pages and ${fileLimit.toLocaleString()} files. Your evidence currently contains ${pages.toLocaleString()} pages and ${files.toLocaleString()} files. ${resolution}`;
  }

  if (overPages) {
    return `Your selected ${tier.toLocaleString()}-page package allows up to ${tier.toLocaleString()} pages. Your evidence currently contains ${pages.toLocaleString()} pages. ${resolution}`;
  }

  return `Your selected ${tier.toLocaleString()}-page package allows up to ${fileLimit.toLocaleString()} files. Your evidence currently contains ${files.toLocaleString()} files. ${resolution}`;
}

function CheckboxGroup({ values, selected, onChange, columns = 2 }) {
  const selectedSet = new Set(Array.isArray(selected) ? selected : []);
  const toggle = (value) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  };
  return <div className="case-analyzer-checkbox-grid" style={{ ...styles.checkboxGrid, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>{values.map((value) => <label key={value} style={styles.checkboxLabel}><input type="checkbox" checked={selectedSet.has(value)} onChange={() => toggle(value)} style={styles.checkbox} /><span>{value}</span></label>)}</div>;
}

function Field({ label, required, help, children }) {
  return <label style={styles.field}><span style={styles.fieldLabel}>{label}{required ? <span style={styles.required}> *</span> : null}</span>{children}{help ? <span style={styles.helpText}>{help}</span> : null}</label>;
}

function SectionHeader({ number, title, text, open, onToggle }) {
  return <button type="button" style={styles.sectionHeaderButton} onClick={onToggle}>
    <div style={styles.sectionHeader}><div style={styles.stepBadge}>{number}</div><div style={styles.sectionHeaderText}><h2 style={styles.sectionTitle}>{title}</h2>{text ? <p style={styles.sectionText}>{text}</p> : null}</div><div style={styles.sectionChevron}>{open ? "−" : "+"}</div></div>
  </button>;
}

function createBundleFromTemplate(template, source = "universal") {
  return {
    bundle_id: makeBundleId(template.id || source),
    template_id: template.id || "other_evidence",
    source,
    category: template.label || "Other evidence / not sure",
    hint: template.hint || "",
    what_it_proves: "",
    supports_side: "Not sure",
    importance: "Not sure",
    evidence_quality: "Not sure",
    date_range: "",
    files: [],
    expanded: true,
  };
}


function preventBundleFocusFootprint(event) {
  if (event && typeof event.preventDefault === "function") event.preventDefault();
}


function clearBundleCardFocusFootprint() {
  if (typeof document === "undefined") return;

  const active = document.activeElement;
  if (active && typeof active.blur === "function") {
    active.blur();
  }

  document
    .querySelectorAll(".case-analyzer-bundle-pick-card, .case-analyzer-bundle-picker-grid button")
    .forEach((node) => {
      if (node && typeof node.blur === "function") {
        node.blur();
      }
    });
}


export default function CaseAnalyzerPage() {
  const navigate = useNavigate();
  const isSmall = useIsSmallScreen();

  const [idToken, setIdToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [jobId, setJobId] = useState(null);
  const idTokenRef = useRef(null);
  const uploadProtectionRef = useRef(false);
  const uploadUiThrottleRef = useRef(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    type: "idle",
    phase: "idle",
    fileNumber: 0,
    totalFiles: 0,
    fileName: "",
    sizeBytes: 0,
    loadedBytes: 0,
    totalBytes: 0,
    percent: 0,
    elapsedMs: 0,
    uploadedCount: 0,
    averageBytesPerSecond: 0,
    fileStartedAt: "",
  });
  const [uploadFailure, setUploadFailure] = useState(null);
  const [uploadClockTick, setUploadClockTick] = useState(0);
  const [openSections, setOpenSections] = useState({ profile: true, position: true, facts: true, evidence: true });

  const [form, setForm] = useState({
    job_name: "", case_type: "", case_subtype: "", user_role: "", opposing_party_type: "", user_has_attorney: "Unknown", other_side_has_attorney: "Unknown",
    country: "United States", state: "", county: "", city: "", postal_code: "", forum: "", court_or_agency_name: "",
    case_stage: "", issue_start_date: "", issue_discovery_date: "", demand_letter_status: "Unknown", filed_status: "Unknown", upcoming_deadline: "", deadline_known: "Unknown",
    desired_result: "", estimated_amount: "", relief_explanation: "", facts_summary: "", other_side_position: "", correspondence_types: [], relief_types: [],
    accepted_terms: false,
  });

  const [evidenceBundles, setEvidenceBundles] = useState([]);
  const [notice, setNotice] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [evidenceError, setEvidenceError] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [billingSnapshot, setBillingSnapshot] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [casePackageOpen, setCasePackageOpen] = useState(true);
  const [selectedPackageTier, setSelectedPackageTier] = useState(null);
  const [confirmedHigherPackageKey, setConfirmedHigherPackageKey] = useState("");
  const [showMoreEvidenceBundles, setShowMoreEvidenceBundles] = useState(false);
  const [bundlePickerResetKey, setBundlePickerResetKey] = useState(0);

  const subtypeOptions = useMemo(() => SUBTYPES_BY_CASE[form.case_type] || [], [form.case_type]);
  const primaryEvidenceBundles = useMemo(() => UNIVERSAL_EVIDENCE_BUNDLES.slice(0, 15), []);
  const additionalEvidenceBundles = useMemo(() => UNIVERSAL_EVIDENCE_BUNDLES.slice(15), []);

  const evidenceStats = useMemo(() => {
    const files = evidenceBundles.flatMap((bundle) => (bundle.files || []).map((fileObj) => ({ ...fileObj, bundle_id: bundle.bundle_id })));
    const totalBytes = files.reduce((sum, item) => sum + Number(item.file?.size || 0), 0);
    const detectedPageCount = files.reduce((sum, item) => sum + getDetectedItemPageCount(item), 0);
    const userEnteredPageCount = files.reduce((sum, item) => sum + getUserEnteredItemPageCount(item), 0);
    const declaredTotalPageCount = detectedPageCount + userEnteredPageCount;
    const missingManualPageCountFiles = files.filter((item) => itemNeedsManualPageCount(item) && getUserEnteredItemPageCount(item) <= 0).length;
    const manualPageCountFiles = files.filter((item) => itemNeedsManualPageCount(item)).length;
    const percent = Math.min(100, Math.round((totalBytes / MAX_TOTAL_BYTES) * 100));
    const pagePercent = Math.min(100, Math.round((declaredTotalPageCount / MAX_TOTAL_PAGES) * 100));
    return {
      files,
      totalBytes,
      detectedPageCount,
      userEnteredPageCount,
      knownPageCount: declaredTotalPageCount,
      totalPageCount: declaredTotalPageCount,
      declaredTotalPageCount,
      unknownPageCountFiles: missingManualPageCountFiles,
      missingManualPageCountFiles,
      manualPageCountFiles,
      percent,
      pagePercent,
      count: files.length,
      remainingBytes: Math.max(0, MAX_TOTAL_BYTES - totalBytes),
      remainingPages: Math.max(0, MAX_TOTAL_PAGES - declaredTotalPageCount),
    };
  }, [evidenceBundles]);

  const activeTemplateIds = useMemo(() => new Set(evidenceBundles.map((b) => b.template_id)), [evidenceBundles]);
  const uploadLocked = evidenceStats.totalBytes >= MAX_TOTAL_BYTES || evidenceStats.totalPageCount >= MAX_TOTAL_PAGES || evidenceStats.count >= MAX_FILES_SAFETY;


  const caseAnalyzerPackageInfo = useMemo(() => {
    const billing = billingSnapshot || {};
    const monthlyEntitlements = billing.monthly_entitlements || billing.monthlyEntitlements || {};
    const addonEntitlements = billing.addon_entitlements || billing.addonEntitlements || billing.purchased_addons || {};

    const monthlyPackages = normalizeCaseAnalyzerPackages(monthlyEntitlements);
    const addonPackages = normalizeCaseAnalyzerPackages(addonEntitlements);
    const combinedPackages = mergeCaseAnalyzerPackages(monthlyPackages, addonPackages);
    const availablePackages = combinedPackages
      .filter((row) => Number(row.remaining || 0) > 0)
      .sort((a, b) => Number(a.page_tier || 0) - Number(b.page_tier || 0));
    const highestTier = getHighestCaseAnalyzerPackageTier(combinedPackages);

    return {
      monthlyPackages,
      addonPackages,
      combinedPackages,
      availablePackages,
      highestTier,
    };
  }, [billingSnapshot]);

  const availablePackageSignature = useMemo(
    () => caseAnalyzerPackageInfo.availablePackages
      .map((row) => `${Number(row.page_tier || 0)}:${Number(row.remaining || 0)}`)
      .join("|"),
    [caseAnalyzerPackageInfo.availablePackages]
  );

  useEffect(() => {
    if (billingLoading) return;

    const available = caseAnalyzerPackageInfo.availablePackages || [];
    if (!available.length) {
      if (selectedPackageTier !== null) setSelectedPackageTier(null);
      return;
    }

    const selectedStillAvailable = available.some(
      (row) => Number(row.page_tier || 0) === Number(selectedPackageTier || 0)
    );

    if (!selectedStillAvailable) {
      const lowest = getLowestAvailablePackage(available);
      setSelectedPackageTier(lowest ? Number(lowest.page_tier || 0) : null);
      setConfirmedHigherPackageKey("");
    }
  }, [availablePackageSignature, billingLoading, selectedPackageTier]);

  const selectedPackage = useMemo(
    () => caseAnalyzerPackageInfo.availablePackages.find(
      (row) => Number(row.page_tier || 0) === Number(selectedPackageTier || 0)
    ) || null,
    [caseAnalyzerPackageInfo.availablePackages, selectedPackageTier]
  );

  const selectedPackagePageLimit = Number(selectedPackage?.page_tier || 0);
  const selectedPackageFileLimit = getCaseAnalyzerFileLimit(selectedPackagePageLimit);
  const selectedPackageLimitError = useMemo(
    () => buildSelectedPackageLimitError({
      selectedTier: selectedPackagePageLimit,
      selectedFileLimit: selectedPackageFileLimit,
      knownPages: evidenceStats.totalPageCount,
      fileCount: evidenceStats.count,
      availablePackages: caseAnalyzerPackageInfo.availablePackages,
    }),
    [
      selectedPackagePageLimit,
      selectedPackageFileLimit,
      evidenceStats.totalPageCount,
      evidenceStats.count,
      caseAnalyzerPackageInfo.availablePackages,
    ]
  );

  const recommendedLowerPackage = useMemo(() => {
    if (
      !selectedPackage ||
      evidenceStats.count <= 0 ||
      evidenceStats.missingManualPageCountFiles > 0
    ) {
      return null;
    }

    const smallestFit = getSmallestFittingAvailablePackage(
      caseAnalyzerPackageInfo.availablePackages,
      evidenceStats.totalPageCount,
      evidenceStats.count
    );

    if (!smallestFit) return null;
    return Number(smallestFit.page_tier || 0) < selectedPackagePageLimit
      ? smallestFit
      : null;
  }, [
    selectedPackage,
    selectedPackagePageLimit,
    caseAnalyzerPackageInfo.availablePackages,
    evidenceStats.totalPageCount,
    evidenceStats.count,
    evidenceStats.missingManualPageCountFiles,
  ]);

  const higherPackageConfirmationKey = recommendedLowerPackage
    ? [
        selectedPackagePageLimit,
        Number(recommendedLowerPackage.page_tier || 0),
        evidenceStats.totalPageCount,
        evidenceStats.count,
      ].join(":")
    : "";

  const higherPackageConfirmationRequired = Boolean(
    recommendedLowerPackage &&
    confirmedHigherPackageKey !== higherPackageConfirmationKey
  );

  const factsSummaryLength = form.facts_summary.trim().length;
  const factsSummaryMeetsMin = factsSummaryLength >= FACTS_SUMMARY_MIN_CHARS;

  const packageUploadLocked = Boolean(!selectedPackage || selectedPackageLimitError);
  const packageCapacityReached = Boolean(
    selectedPackage &&
    (
      evidenceStats.count >= selectedPackageFileLimit ||
      evidenceStats.totalPageCount >= selectedPackagePageLimit
    )
  );
  const packageAddLocked = packageUploadLocked || packageCapacityReached;
  const manualPageCountLocked = evidenceStats.missingManualPageCountFiles > 0;
  const manualPageCountError = manualPageCountLocked
    ? `Please enter the page count for ${evidenceStats.missingManualPageCountFiles} file${evidenceStats.missingManualPageCountFiles === 1 ? "" : "s"} where pages could not be detected.`
    : "";

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!uploadProtectionRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!busy || !uploadProgress.fileStartedAt) return undefined;
    setUploadClockTick(Date.now());
    const timer = window.setInterval(() => setUploadClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [busy, uploadProgress.fileStartedAt]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        idTokenRef.current = null;
        setIdToken(null);
        setIsLoggedIn(false);
        setBillingSnapshot(null);
        setBillingError("");
        return;
      }

      try {
        const token = await user.getIdToken();
        idTokenRef.current = token;
        setIdToken(token);
        setIsLoggedIn(true);
        fetchBillingSnapshot(token);
      } catch (err) {
        console.error("Failed to fetch Firebase ID token for Case Analyzer:", err);
        idTokenRef.current = null;
        setIdToken(null);
        setIsLoggedIn(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const getFreshAuthToken = async ({ forceRefresh = false } = {}) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      idTokenRef.current = null;
      setIdToken(null);
      setIsLoggedIn(false);
      throw new Error("Please log in to use Case Analyzer.");
    }

    const token = await currentUser.getIdToken(forceRefresh);
    idTokenRef.current = token;
    setIdToken(token);
    setIsLoggedIn(true);
    return token;
  };


  const fetchBillingSnapshot = async (tokenOverride) => {
    const token = tokenOverride || idTokenRef.current || idToken;
    if (!token) {
      setBillingSnapshot(null);
      return null;
    }

    setBillingLoading(true);
    setBillingError("");

    try {
      const res = await fetch(`${HOST}/user/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      setBillingSnapshot(data || {});
      return data || {};
    } catch (err) {
      console.error("Case Analyzer billing fetch error:", err);
      setBillingSnapshot(null);
      setBillingError(err?.message || "Unable to load Case Analyzer package information.");
      return null;
    } finally {
      setBillingLoading(false);
    }
  };

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setFieldErrors((prev) => { const next = { ...prev }; Object.keys(patch).forEach((k) => delete next[k]); return next; });
  };

  const flattenedEvidenceItems = useMemo(() => {
    const items = [];
    evidenceBundles.forEach((bundle) => {
      (bundle.files || []).forEach((fileItem, idx) => {
        items.push({
          ...fileItem,
          client_id: fileItem.client_id || `${bundle.bundle_id}_file_${idx + 1}`,
          evidence_type: bundle.category || "Evidence",
          evidence_note: bundle.what_it_proves || "",
          evidence_bundle_id: bundle.bundle_id,
          template_id: bundle.template_id,
          templateId: bundle.template_id,
          evidence_bundle_category: bundle.category,
          supports_side: bundle.supports_side,
          importance: bundle.importance,
          evidence_quality: bundle.evidence_quality,
          date_range: bundle.date_range,
          page_count: getItemPageCount(fileItem),
          pageCount: getItemPageCount(fileItem),
          detected_page_count: getDetectedItemPageCount(fileItem),
          detectedPageCount: getDetectedItemPageCount(fileItem),
          manual_page_count: getUserEnteredItemPageCount(fileItem),
          manualPageCount: getUserEnteredItemPageCount(fileItem),
          manual_page_count_required: itemNeedsManualPageCount(fileItem),
          manualPageCountRequired: itemNeedsManualPageCount(fileItem),
          page_count_status: itemNeedsManualPageCount(fileItem) && getItemPageCount(fileItem) > 0 ? "user_provided" : getItemPageCountStatus(fileItem),
          pageCountStatus: itemNeedsManualPageCount(fileItem) && getItemPageCount(fileItem) > 0 ? "user_provided" : getItemPageCountStatus(fileItem),
          page_count_source: itemNeedsManualPageCount(fileItem) && getItemPageCount(fileItem) > 0 ? "manual_user_entry" : (fileItem.page_count_source || fileItem.pageCountSource || ""),
          pageCountSource: itemNeedsManualPageCount(fileItem) && getItemPageCount(fileItem) > 0 ? "manual_user_entry" : (fileItem.pageCountSource || fileItem.page_count_source || ""),
        });
      });
    });
    return items;
  }, [evidenceBundles]);

  const buildPayload = () => ({
    language: "auto",
    job_name: form.job_name || makeJobName(form),
    case_profile: { case_type: form.case_type, case_subtype: form.case_subtype, user_role: form.user_role, opposing_party_type: form.opposing_party_type, user_has_attorney: form.user_has_attorney, other_side_has_attorney: form.other_side_has_attorney },
    location: { country: form.country, state: form.state, county: form.county, city: form.city, postal_code: form.postal_code, forum: form.forum, court_or_agency_name: form.court_or_agency_name },
    case_position: { case_stage: form.case_stage, issue_start_date: form.issue_start_date, issue_discovery_date: form.issue_discovery_date, demand_letter_status: form.demand_letter_status, filed_status: form.filed_status, deadline_known: form.deadline_known, upcoming_deadline: form.upcoming_deadline },
    facts: { desired_result: form.desired_result, estimated_amount: normalizeNumber(form.estimated_amount), estimated_amount_raw: form.estimated_amount, relief_explanation: form.relief_explanation, facts_summary: form.facts_summary, other_side_position: form.other_side_position, correspondence_types: form.correspondence_types, relief_types: form.relief_types },
    case_analyzer_package: {
      package_pages: selectedPackagePageLimit || null,
      page_limit: selectedPackagePageLimit || null,
      file_limit: selectedPackageFileLimit || null,
      selected_page_tier: selectedPackagePageLimit || null,
      selected_file_limit: selectedPackageFileLimit || null,
      selected_by_user: Boolean(selectedPackage),
      selection_source: "frontend_user_selection",
      remaining_reports_snapshot: Number(selectedPackage?.remaining || 0),
    },
    evidence_package: {
      total_file_count: evidenceStats.count,
      total_upload_bytes: evidenceStats.totalBytes,
      total_upload_mb: Number((evidenceStats.totalBytes / (1024 * 1024)).toFixed(2)),
      total_page_count: evidenceStats.totalPageCount,
      known_page_count: evidenceStats.totalPageCount,
      detected_page_count: evidenceStats.detectedPageCount,
      user_entered_page_count: evidenceStats.userEnteredPageCount,
      declared_total_page_count: evidenceStats.declaredTotalPageCount,
      unknown_page_count_files: evidenceStats.unknownPageCountFiles,
      missing_manual_page_count_files: evidenceStats.missingManualPageCountFiles,
      max_upload_mb: MAX_TOTAL_UPLOAD_MB,
      per_file_max_mb: MAX_FILE_MB,
      max_total_pages: MAX_TOTAL_PAGES,
      max_files_safety: MAX_FILES_SAFETY,
      requested_file_count: evidenceStats.count,
      selected_case_analyzer_page_tier: selectedPackagePageLimit || null,
      selected_case_analyzer_file_limit: selectedPackageFileLimit || null,
      matched_case_analyzer_page_tier: selectedPackagePageLimit || null,
      max_available_case_analyzer_page_tier: caseAnalyzerPackageInfo.highestTier || 0,
    },
    billing_package: {
      product: "case_analyzer",
      known_page_count: evidenceStats.totalPageCount,
      detected_page_count: evidenceStats.detectedPageCount,
      user_entered_page_count: evidenceStats.userEnteredPageCount,
      declared_total_page_count: evidenceStats.declaredTotalPageCount,
      requested_page_count: evidenceStats.totalPageCount,
      requested_file_count: evidenceStats.count,
      selected_page_tier: selectedPackagePageLimit || null,
      selected_file_limit: selectedPackageFileLimit || null,
      selected_by_user: Boolean(selectedPackage),
      selection_source: "frontend_user_selection",
      unknown_page_count_files: evidenceStats.unknownPageCountFiles,
      missing_manual_page_count_files: evidenceStats.missingManualPageCountFiles,
      matched_page_tier: selectedPackagePageLimit || null,
      max_available_page_tier: caseAnalyzerPackageInfo.highestTier || 0,
      available_packages: caseAnalyzerPackageInfo.availablePackages,
    },
    evidence_bundles: evidenceBundles.map((bundle) => ({
      bundle_id: bundle.bundle_id,
      template_id: bundle.template_id,
      source: bundle.source,
      category: bundle.category,
      what_it_proves: bundle.what_it_proves,
      supports_side: bundle.supports_side,
      importance: bundle.importance,
      evidence_quality: bundle.evidence_quality,
      date_range: bundle.date_range,
      file_count: (bundle.files || []).length,
      files: (bundle.files || []).map((item, idx) => ({
        client_id: item.client_id || `${bundle.bundle_id}_file_${idx + 1}`,
        file_name: item.file?.name || "",
        file_size: item.file?.size || null,
        file_type: item.file?.type || "",
        page_count: getItemPageCount(item),
        pageCount: getItemPageCount(item),
        detected_page_count: getDetectedItemPageCount(item),
        detectedPageCount: getDetectedItemPageCount(item),
        manual_page_count: getUserEnteredItemPageCount(item),
        manualPageCount: getUserEnteredItemPageCount(item),
        manual_page_count_required: itemNeedsManualPageCount(item),
        manualPageCountRequired: itemNeedsManualPageCount(item),
        page_count_status: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "user_provided" : getItemPageCountStatus(item),
        pageCountStatus: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "user_provided" : getItemPageCountStatus(item),
        page_count_source: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "manual_user_entry" : (item.page_count_source || item.pageCountSource || ""),
        pageCountSource: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "manual_user_entry" : (item.pageCountSource || item.page_count_source || ""),
      })),
    })),
    evidence_summary: {
      evidence_count: flattenedEvidenceItems.length,
      requested_file_count: evidenceStats.count,
      selected_page_tier: selectedPackagePageLimit || null,
      selected_file_limit: selectedPackageFileLimit || null,
      total_page_count: evidenceStats.totalPageCount,
      detected_page_count: evidenceStats.detectedPageCount,
      user_entered_page_count: evidenceStats.userEnteredPageCount,
      declared_total_page_count: evidenceStats.declaredTotalPageCount,
      unknown_page_count_files: evidenceStats.unknownPageCountFiles,
      missing_manual_page_count_files: evidenceStats.missingManualPageCountFiles,
      evidence_items: flattenedEvidenceItems.map((item, idx) => ({
        client_id: item.client_id || `evidence_${idx + 1}`,
        evidence_type: item.evidence_type || "",
        evidence_note: item.evidence_note || "",
        evidence_bundle_id: item.evidence_bundle_id || "",
        template_id: item.template_id || item.templateId || "",
        templateId: item.templateId || item.template_id || "",
        evidence_bundle_category: item.evidence_bundle_category || "",
        supports_side: item.supports_side || "",
        importance: item.importance || "",
        evidence_quality: item.evidence_quality || "",
        file_name: item.file?.name || "",
        file_size: item.file?.size || null,
        page_count: getItemPageCount(item),
        pageCount: getItemPageCount(item),
        detected_page_count: getDetectedItemPageCount(item),
        detectedPageCount: getDetectedItemPageCount(item),
        manual_page_count: getUserEnteredItemPageCount(item),
        manualPageCount: getUserEnteredItemPageCount(item),
        manual_page_count_required: itemNeedsManualPageCount(item),
        manualPageCountRequired: itemNeedsManualPageCount(item),
        page_count_status: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "user_provided" : getItemPageCountStatus(item),
        pageCountStatus: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "user_provided" : getItemPageCountStatus(item),
        page_count_source: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "manual_user_entry" : (item.page_count_source || item.pageCountSource || ""),
        pageCountSource: itemNeedsManualPageCount(item) && getItemPageCount(item) > 0 ? "manual_user_entry" : (item.pageCountSource || item.page_count_source || ""),
      })),
    },
    accepted_terms: !!form.accepted_terms,
  });

  const ensureJob = async (tokenOverride) => {
    const token = tokenOverride || idTokenRef.current || idToken;

    if (!token) throw new Error("Please log in to use Case Analyzer.");

    // The launcher does not reuse a prior intake/upload job. Every user-started
    // attempt creates a fresh job, while the completed /analyze handoff remains
    // owned by the existing Status page.
    const data = await createCaseAnalyzerJob(buildPayload(), { idToken: token });
    const id = data?.job_id || data?.jobId || data?.id;

    if (!id) throw new Error("Backend did not return a job id.");

    setJobId(id);
    return id;
  };

  const allowStatusHandoff = (id) => {
    if (!id) return;
    try {
      sessionStorage.setItem(`caseAnalyzerStatus:${id}`, "allowed");
    } catch (_) {
      // Ignore sessionStorage errors; route state below still protects normal navigation.
    }
  };

  const goToStatusPage = (id, options = {}) => {
    if (!id) return;
    allowStatusHandoff(id);
    navigate(`/case-analyzer/status?jobId=${encodeURIComponent(id)}`, {
      replace: options.replace !== false,
      state: {
        fromCaseAnalyzer: true,
        jobId: id,
      },
    });
  };

  const lockLauncherForAnalysis = () => {
    // Intentional no-op placeholder for the handoff boundary.
    // The launcher has no autosave/draft mode.
  };

  const focusValidationTarget = (targetId, sectionKey) => {
    if (sectionKey) {
      setOpenSections((prev) => ({ ...prev, [sectionKey]: true }));
    }

    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      const focusable = target.matches?.("input,select,textarea,button")
        ? target
        : target.querySelector?.("input,select,textarea,button");
      window.setTimeout(() => focusable?.focus?.({ preventScroll: true }), 260);
    }, sectionKey ? 80 : 20);
  };

  const validateBeforeRun = () => {
    const next = {};
    if (!form.case_type) next.case_type = "Please select a case type, or choose Not Sure.";
    if (!form.case_subtype) next.case_subtype = "Please select a case subtype.";
    if (!form.user_role) next.user_role = "Please select your role.";
    if (!form.country) next.country = "Please enter the country.";
    if (form.country === "United States" && !form.state) next.state = "Please enter the state.";
    if (!form.forum) next.forum = "Please select the forum or choose Unknown.";
    if (!form.case_stage) next.case_stage = "Please select the case stage.";
    if (!form.facts_summary || form.facts_summary.trim().length < FACTS_SUMMARY_MIN_CHARS) next.facts_summary = `Please write at least ${FACTS_SUMMARY_MIN_CHARS} characters so the report has enough facts to analyze.`;
    if (!form.accepted_terms) next.accepted_terms = "Please agree to the Terms of Use and Privacy Policy.";

    const bundlesWithFiles = evidenceBundles.filter((bundle) => (bundle.files || []).length > 0);
    const emptySelectedBundles = evidenceBundles.filter((bundle) => (bundle.files || []).length === 0);
    let evidenceMsg = "";
    if (!bundlesWithFiles.length) evidenceMsg = "Please add at least one evidence file before running Case Analyzer.";
    else if (emptySelectedBundles.length) evidenceMsg = "Please add files to each selected evidence bundle, or remove unused bundles.";
    else if (evidenceStats.missingManualPageCountFiles > 0) evidenceMsg = `Please enter the page count for ${evidenceStats.missingManualPageCountFiles} file${evidenceStats.missingManualPageCountFiles === 1 ? "" : "s"} where pages could not be detected.`;
    else if (evidenceStats.totalBytes > MAX_TOTAL_BYTES) evidenceMsg = `Your evidence package is over the ${MAX_TOTAL_UPLOAD_MB} MB limit. Please remove lower-priority files.`;
    else if (evidenceStats.count > MAX_FILES_SAFETY) evidenceMsg = `Your evidence package has more than ${MAX_FILES_SAFETY} files. Please remove lower-priority files.`;
    else if (evidenceStats.totalPageCount > MAX_TOTAL_PAGES) evidenceMsg = `Your evidence package is over the ${MAX_TOTAL_PAGES}-page limit. Please remove lower-priority files.`;
    else if (!selectedPackage) evidenceMsg = "Please select one of your available Case Analyzer packages.";
    else if (selectedPackageLimitError) evidenceMsg = selectedPackageLimitError;
    else if (higherPackageConfirmationRequired) evidenceMsg = `Your evidence also fits your available ${Number(recommendedLowerPackage?.page_tier || 0).toLocaleString()}-page package. Please select that lower package or confirm that you want to use the selected ${selectedPackagePageLimit.toLocaleString()}-page package.`;
    setEvidenceError(evidenceMsg);
    setFieldErrors(next);

    const validationOrder = [
      ["case_type", "case-analyzer-field-case_type", "profile"],
      ["case_subtype", "case-analyzer-field-case_subtype", "profile"],
      ["user_role", "case-analyzer-field-user_role", "profile"],
      ["country", "case-analyzer-field-country", "position"],
      ["state", "case-analyzer-field-state", "position"],
      ["forum", "case-analyzer-field-forum", "position"],
      ["case_stage", "case-analyzer-field-case_stage", "position"],
      ["facts_summary", "case-analyzer-field-facts_summary", "facts"],
    ];

    const firstFieldError = validationOrder.find(([key]) => next[key]);
    if (firstFieldError) {
      focusValidationTarget(firstFieldError[1], firstFieldError[2]);
      return false;
    }

    if (evidenceMsg) {
      focusValidationTarget("case-analyzer-field-evidence", "evidence");
      return false;
    }

    if (next.accepted_terms) {
      focusValidationTarget("case-analyzer-field-accepted_terms");
      return false;
    }

    return true;
  };

  const handleUploadEvent = (event = {}) => {
    const now = Date.now();
    const shouldThrottle = event.type === "upload_progress" && Number(event.percent || 0) < 100;
    if (shouldThrottle && now - uploadUiThrottleRef.current < 120) return;
    uploadUiThrottleRef.current = now;

    setUploadProgress((previous) => {
      const isNewFile = event.type === "file_start";
      return {
        ...previous,
        ...event,
        fileNumber: Number(event.fileNumber || event.fileIndex || previous.fileNumber || 0),
        totalFiles: Number(event.totalFiles || previous.totalFiles || evidenceStats.count || 0),
        percent: Math.max(0, Math.min(100, Number(event.percent ?? (isNewFile ? 0 : previous.percent) ?? 0))),
        loadedBytes: Math.max(0, Number(event.loadedBytes ?? (isNewFile ? 0 : previous.loadedBytes) ?? 0)),
        totalBytes: Math.max(0, Number(event.totalBytes ?? event.sizeBytes ?? previous.totalBytes ?? previous.sizeBytes ?? 0)),
        averageBytesPerSecond: Math.max(0, Number(event.averageBytesPerSecond ?? (isNewFile ? 0 : previous.averageBytesPerSecond) ?? 0)),
      };
    });
    setUploadPhase(uploadPhaseMessage(event));
  };

  const uploadEvidenceIfNeeded = async (id, tokenOverride) => {
    if (!flattenedEvidenceItems.length) return null;

    const token = tokenOverride || idTokenRef.current || idToken;
    if (!token) throw new Error("Please log in to upload evidence.");

    const payload = buildPayload();
    return uploadCaseAnalyzerEvidence(id, flattenedEvidenceItems, {
      idToken: token,
      payload,
      caseAnalyzerPayload: payload,
      onUploadEvent: handleUploadEvent,
    });
  };

  const requestFailedJobAbandonment = (failedJobId, err, tokenOverride) => {
    if (!failedJobId) return;
    const token = tokenOverride || idTokenRef.current || idToken;
    if (!token) return;

    abandonCaseAnalyzerJob(
      failedJobId,
      {
        reason: "frontend_pre_analysis_failure",
        failureStage: err?.failureStage || err?.uploadStage || err?.phase || "pre_analysis_failure",
        fileName: err?.fileName || "",
        fileNumber: Number(err?.fileNumber || err?.fileIndex || 0),
        totalFiles: Number(err?.totalFiles || evidenceStats.count || flattenedEvidenceItems.length || 0),
        elapsedMs: Number(err?.elapsedMs || err?.timings?.total_ms || 0),
        userMessage: getCaseAnalyzerUploadUserMessage(err),
      },
      { idToken: token }
    ).catch((abandonError) => {
      // The user can start a new job immediately. Backend cleanup/abandonment
      // is best-effort here and must never block the fresh attempt.
      console.warn("Unable to mark failed Case Analyzer upload job as abandoned:", abandonError);
    });
  };

  const handleAnalyze = async () => {
    setNotice(null);
    setUploadFailure(null);
    setUploadProgress({
      type: "idle",
      phase: "idle",
      fileNumber: 0,
      totalFiles: evidenceStats.count,
      fileName: "",
      sizeBytes: 0,
      loadedBytes: 0,
      totalBytes: 0,
      percent: 0,
      elapsedMs: 0,
      uploadedCount: 0,
      averageBytesPerSecond: 0,
      fileStartedAt: "",
    });

    let freshIdToken = null;
    try {
      freshIdToken = await getFreshAuthToken({ forceRefresh: true });
    } catch (err) {
      navigate("/login", { state: { from: "/case-analyzer" } });
      return;
    }

    if (!validateBeforeRun()) return;

    uploadProtectionRef.current = true;
    setUploadPhase("Preparing your secure upload…");
    setBusy(true);

    let id = null;
    let analyzeRequestSubmitted = false;

    try {
      setUploadPhase("Creating your Case Analyzer workspace…");
      id = await ensureJob(freshIdToken);

      setUploadPhase(`Preparing ${evidenceStats.count.toLocaleString()} evidence file${evidenceStats.count === 1 ? "" : "s"}…`);
      await uploadEvidenceIfNeeded(id, freshIdToken);

      // From this point forward, the launcher is locked and hands off to the
      // status/dashboard page. There is no autosave or draft PATCH behavior.
      lockLauncherForAnalysis();

      setUploadPhase("Upload complete. Starting your case analysis…");
      analyzeRequestSubmitted = true;
      let started = null;
      try {
        started = await startCaseAnalyzerRun(id, buildPayload(), { idToken: freshIdToken });
      } catch (err) {
        if (!isAlreadyLockedAnalyzeError(err)) throw err;

        // If a duplicate click or race condition says the job is already locked,
        // the correct UX is still to move to the status page for this job.
        started = err?.job || err?.response?.job || err?.response || { job_id: id };
      }

      const startedJobId = started?.job_id || started?.jobId || started?.id || id;

      if (!startedJobId) throw new Error("Backend did not return a job id.");

      setJobId(startedJobId);
      setJob({
        job_id: startedJobId,
        jobId: startedJobId,
        status: "queued",
        progress: {
          stage: "queued",
          percent: 5,
          message: "Queued for analysis.",
        },
        message: "Queued for analysis.",
      });

      uploadProtectionRef.current = false;
      setUploadPhase("");
      goToStatusPage(startedJobId);
      return;
    } catch (err) {
      uploadProtectionRef.current = false;
      setUploadPhase("");
      setBusy(false);

      // Once /analyze has been submitted, ownership belongs to the existing
      // Status page even when the browser did not receive a clean response.
      if (id && (analyzeRequestSubmitted || isAlreadyLockedAnalyzeError(err))) {
        lockLauncherForAnalysis();
        goToStatusPage(id);
        return;
      }

      const userMessage = err?.uploadStage || err?.failureStage
        ? getCaseAnalyzerUploadUserMessage(err)
        : (err?.message || "The upload could not be completed.");
      const failedJobId = id;

      // Never reuse a failed intake/upload job. Keep the local form and File
      // objects, clear only the backend job identity, and let the user decide
      // when to click Try Again.
      setJobId(null);
      setJob(null);
      setUploadFailure({
        jobId: failedJobId,
        fileName: err?.fileName || "",
        failureStage: err?.failureStage || err?.uploadStage || err?.phase || "pre_analysis_failure",
        message: userMessage,
      });
      setNotice({
        type: "error",
        msg: `${userMessage} Your form and selected files are still here. Click Try Again to start a new upload session.`,
      });

      requestFailedJobAbandonment(failedJobId, err, freshIdToken);
    }
  };

  const addBundle = (template, source = "universal") => {
    setEvidenceError("");
    setEvidenceBundles((prev) => {
      const existing = prev.find((bundle) => bundle.template_id === template.id);
      if (existing) {
        return prev.map((bundle) => ({ ...bundle, expanded: bundle.template_id === template.id }));
      }
      return [
        ...prev.map((bundle) => ({ ...bundle, expanded: false })),
        createBundleFromTemplate(template, source),
      ];
    });
    setTimeout(() => {
      const el = document.getElementById("selected-evidence-bundles");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const removeBundle = (bundleId) => {
    clearBundleCardFocusFootprint();
    setEvidenceBundles((prev) => prev.filter((bundle) => bundle.bundle_id !== bundleId));
    setBundlePickerResetKey((prev) => prev + 1);
    window.setTimeout(clearBundleCardFocusFootprint, 0);
  };

  const updateBundle = (bundleId, patch) => {
    setEvidenceBundles((prev) => prev.map((bundle) => {
      if (bundle.bundle_id !== bundleId) {
        return patch && patch.expanded === true ? { ...bundle, expanded: false } : bundle;
      }
      return { ...bundle, ...patch };
    }));
  };


  const addFilesToBundle = async (bundleId, fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    if (!selectedPackage || !selectedPackagePageLimit || !selectedPackageFileLimit) {
      setEvidenceError("Please select one of your available Case Analyzer packages before adding evidence files.");
      return;
    }

    const accepted = [];
    let errorMsg = "";
    let rejectedByFileLimit = 0;
    let runningTotalBytes = evidenceStats.totalBytes;
    let runningTotalPages = evidenceStats.totalPageCount;
    let runningFileCount = evidenceStats.count;

    for (const file of incoming) {
      if (runningFileCount >= selectedPackageFileLimit) {
        rejectedByFileLimit += 1;
        continue;
      }

      if (file.size > MAX_FILE_BYTES) {
        errorMsg = `${file.name} is larger than ${MAX_FILE_MB} MB. Please upload a smaller file or split/compress it.`;
        continue;
      }

      if (runningTotalBytes + file.size > MAX_TOTAL_BYTES) {
        errorMsg = `Your evidence package reached the ${MAX_TOTAL_UPLOAD_MB} MB limit. Please upload the most important files or remove lower-priority evidence.`;
        continue;
      }

      const pageInfo = await getFrontendPageInfo(file);
      const pageCount = Number(pageInfo.page_count || pageInfo.pageCount || 0) || 0;

      if (pageCount > 0 && runningTotalPages + pageCount > MAX_TOTAL_PAGES) {
        errorMsg = `Adding ${file.name} would bring the evidence package to ${(runningTotalPages + pageCount).toLocaleString()} known page(s), over the ${MAX_TOTAL_PAGES.toLocaleString()}-page safety limit. Please remove lower-priority evidence.`;
        continue;
      }

      const nextKnownPages = runningTotalPages + pageCount;
      if (pageCount > 0 && nextKnownPages > selectedPackagePageLimit) {
        const higherAvailable = caseAnalyzerPackageInfo.availablePackages.some(
          (row) => Number(row.page_tier || 0) > selectedPackagePageLimit
        );
        errorMsg = `Adding ${file.name} would bring your evidence to ${nextKnownPages.toLocaleString()} pages, above the ${selectedPackagePageLimit.toLocaleString()}-page limit of your selected package. Please remove lower-priority files${higherAvailable ? " or select an available higher package" : ""}.`;
        continue;
      }

      runningTotalBytes += file.size;
      runningTotalPages += pageCount;
      runningFileCount += 1;

      const needsManualPageCount = pageCount <= 0;
      accepted.push({
        client_id: makeBundleId("file"),
        file,
        ...pageInfo,
        detected_page_count: needsManualPageCount ? 0 : pageCount,
        detectedPageCount: needsManualPageCount ? 0 : pageCount,
        manual_page_count_required: needsManualPageCount,
        manualPageCountRequired: needsManualPageCount,
        manual_page_count_input: "",
        manualPageCountInput: "",
        manual_page_count: 0,
        manualPageCount: 0,
        page_count_status: needsManualPageCount ? "manual_required" : pageInfo.page_count_status,
        pageCountStatus: needsManualPageCount ? "manual_required" : pageInfo.pageCountStatus,
        page_count_source: needsManualPageCount ? "manual_user_entry_pending" : pageInfo.page_count_source,
        pageCountSource: needsManualPageCount ? "manual_user_entry_pending" : pageInfo.pageCountSource,
      });
    }

    if (rejectedByFileLimit > 0) {
      const higherAvailable = caseAnalyzerPackageInfo.availablePackages.some(
        (row) => Number(row.page_tier || 0) > selectedPackagePageLimit
      );
      errorMsg = `Your selected ${selectedPackagePageLimit.toLocaleString()}-page package allows up to ${selectedPackageFileLimit.toLocaleString()} files. The additional ${rejectedByFileLimit.toLocaleString()} file${rejectedByFileLimit === 1 ? " was" : "s were"} not added. Please remove lower-priority files${higherAvailable ? " or select an available higher package" : ""}.`;
    }

    if (errorMsg) setEvidenceError(errorMsg);
    else setEvidenceError("");

    if (!accepted.length) return;
    setEvidenceBundles((prev) => prev.map((bundle) => (
      bundle.bundle_id === bundleId
        ? { ...bundle, files: [...(bundle.files || []), ...accepted], expanded: true }
        : { ...bundle, expanded: false }
    )));
  };

  const removeFileFromBundle = (bundleId, clientId) => {
    setEvidenceError("");
    setEvidenceBundles((prev) => prev.map((bundle) => bundle.bundle_id === bundleId ? { ...bundle, files: (bundle.files || []).filter((item) => item.client_id !== clientId) } : bundle));
  };

  const updateManualPageCount = (bundleId, clientId, value) => {
    const raw = String(value ?? "").replace(/[^0-9]/g, "");
    const manualPages = normalizePageCountInput(raw);
    setEvidenceError("");
    setEvidenceBundles((prev) => prev.map((bundle) => {
      if (bundle.bundle_id !== bundleId) return bundle;
      return {
        ...bundle,
        files: (bundle.files || []).map((item) => {
          if (item.client_id !== clientId) return item;
          return {
            ...item,
            manual_page_count_required: true,
            manualPageCountRequired: true,
            manual_page_count_input: raw,
            manualPageCountInput: raw,
            manual_page_count: manualPages,
            manualPageCount: manualPages,
            page_count: manualPages,
            pageCount: manualPages,
            page_count_status: manualPages > 0 ? "user_provided" : "manual_required",
            pageCountStatus: manualPages > 0 ? "user_provided" : "manual_required",
            page_count_source: manualPages > 0 ? "manual_user_entry" : "manual_user_entry_pending",
            pageCountSource: manualPages > 0 ? "manual_user_entry" : "manual_user_entry_pending",
          };
        }),
      };
    }));
  };

  const selectPackageTier = (tier) => {
    const nextTier = Number(tier || 0);
    const available = caseAnalyzerPackageInfo.availablePackages.some(
      (row) => Number(row.page_tier || 0) === nextTier && Number(row.remaining || 0) > 0
    );
    if (!available) return;

    setSelectedPackageTier(nextTier);
    setConfirmedHigherPackageKey("");
    setEvidenceError("");
  };

  const useRecommendedLowerPackage = () => {
    if (!recommendedLowerPackage) return;
    selectPackageTier(Number(recommendedLowerPackage.page_tier || 0));
  };

  const confirmSelectedHigherPackage = () => {
    if (!higherPackageConfirmationKey) return;
    setConfirmedHigherPackageKey(higherPackageConfirmationKey);
    setEvidenceError("");
  };

  const progress = Number(job?.progress ?? 0);
  const parsedFileStart = uploadProgress.fileStartedAt ? Date.parse(uploadProgress.fileStartedAt) : 0;
  const liveUploadElapsedMs = busy && parsedFileStart
    ? Math.max(Number(uploadProgress.elapsedMs || 0), Math.max(0, uploadClockTick - parsedFileStart))
    : Number(uploadProgress.elapsedMs || 0);
  const currentFilePercent = Math.max(0, Math.min(100, Number(uploadProgress.percent || 0)));
  const currentFileLoadedBytes = Math.max(0, Number(uploadProgress.loadedBytes || 0));
  const currentFileTotalBytes = Math.max(0, Number(uploadProgress.totalBytes || uploadProgress.sizeBytes || 0));
  const currentUploadSpeed = Math.max(0, Number(uploadProgress.averageBytesPerSecond || 0));
  const showFileUploadDetails = Boolean(uploadProgress.fileNumber && uploadProgress.totalFiles);
  const analyzeButtonLabel = busy
    ? "Uploading evidence…"
    : uploadFailure
      ? "Try Again"
      : isLoggedIn
        ? "Analyze case"
        : "Log in to analyze";
  const analysisDisabled = Boolean(
    busy ||
    evidenceStats.totalBytes > MAX_TOTAL_BYTES ||
    evidenceStats.totalPageCount > MAX_TOTAL_PAGES ||
    evidenceStats.count > MAX_FILES_SAFETY ||
    packageUploadLocked ||
    manualPageCountLocked ||
    higherPackageConfirmationRequired
  );

  return (
    <div className="case-analyzer-page-root" style={{ ...styles.outerWrap, ...(isSmall ? styles.outerWrapMobile : null) }}>
      <style>{`.case-analyzer-input:focus,.case-analyzer-select:focus,.case-analyzer-textarea:focus{border:1px solid #2563EB!important;box-shadow:0 0 0 4px rgba(37,99,235,.16)!important;outline:none!important}.case-analyzer-linkish a{color:#2563EB;font-weight:900;text-decoration:none}.case-analyzer-linkish a:hover{text-decoration:underline}.case-analyzer-bundle-picker-grid button,.case-analyzer-bundle-pick-card{outline:none!important;-webkit-tap-highlight-color:transparent}.case-analyzer-bundle-picker-grid button:focus,.case-analyzer-bundle-picker-grid button:focus-visible,.case-analyzer-bundle-picker-grid button:active,.case-analyzer-bundle-pick-card:focus,.case-analyzer-bundle-pick-card:focus-visible,.case-analyzer-bundle-pick-card:active{outline:none!important;box-shadow:none!important}.case-analyzer-bundle-pick-card:not(.is-added-bundle){border-color:rgba(15,23,42,0.10)!important;box-shadow:none!important}.case-analyzer-bundle-pick-card.is-added-bundle{border-color:rgba(5,150,105,0.30)!important}@media(max-width:1023px){html,body,#root{max-width:100%;overflow-x:hidden}.case-analyzer-page-root,.case-analyzer-page-root *{box-sizing:border-box}.case-analyzer-page-root{width:100%;max-width:100%;overflow-x:hidden}.case-analyzer-page-root img,.case-analyzer-page-root video,.case-analyzer-page-root canvas,.case-analyzer-page-root svg{max-width:100%}.case-analyzer-page-root input,.case-analyzer-page-root select,.case-analyzer-page-root textarea,.case-analyzer-page-root button{max-width:100%;min-width:0}.case-analyzer-page-root p,.case-analyzer-page-root span,.case-analyzer-page-root div,.case-analyzer-page-root h1,.case-analyzer-page-root h2,.case-analyzer-page-root h3{overflow-wrap:anywhere;word-break:break-word}.case-analyzer-main-grid,.case-analyzer-field-grid,.case-analyzer-score-preview,.case-analyzer-feature-grid{grid-template-columns:minmax(0,1fr)!important}.case-analyzer-main-grid>*{min-width:0}.case-analyzer-file-row{grid-template-columns:minmax(0,1fr) auto!important}.case-analyzer-bundle-picker-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}}@keyframes caseAnalyzerSpin{to{transform:rotate(360deg)}}@keyframes caseAnalyzerUploadMove{0%{transform:translateX(-100%);background-position:0 0}100%{transform:translateX(245%);background-position:200% 0}}@media(max-width:640px){.case-analyzer-checkbox-grid,.case-analyzer-bundle-picker-grid{grid-template-columns:minmax(0,1fr)!important}.case-analyzer-file-row{grid-template-columns:minmax(0,1fr)!important}.case-analyzer-bundle-file-top,.case-analyzer-package-top{align-items:stretch!important;flex-direction:column!important}}`}</style>
      {busy && uploadPhase ? (
        <div style={styles.uploadModalOverlay} role="dialog" aria-modal="true" aria-labelledby="case-analyzer-upload-title">
          <div style={{ ...styles.uploadModalCard, ...(isSmall ? styles.uploadModalCardMobile : null) }}>
            <div style={styles.uploadModalGlow} aria-hidden="true" />
            <div style={styles.uploadModalIconWrap}><span style={styles.uploadModalSpinner} aria-hidden="true" /></div>
            <div id="case-analyzer-upload-title" style={styles.uploadModalTitle}>Uploading your case securely</div>
            <p style={styles.uploadModalText}>Please keep this page open. Do not close, refresh, or use the browser’s Back button until you are taken to the Case Analyzer status page.</p>
            <div style={styles.uploadModalStatus}>{uploadPhase}</div>

            {showFileUploadDetails ? (
              <div style={styles.uploadModalFileCard}>
                <div style={styles.uploadModalFileTop}>
                  <span>File {Number(uploadProgress.fileNumber).toLocaleString()} of {Number(uploadProgress.totalFiles).toLocaleString()}</span>
                  <span>{currentFilePercent}%</span>
                </div>
                <div style={styles.uploadModalFileName} title={uploadProgress.fileName || ""}>{uploadProgress.fileName || "Evidence file"}</div>
                <div style={styles.uploadModalStats}>
                  <span>{formatBytes(currentFileLoadedBytes)} of {formatBytes(currentFileTotalBytes)}</span>
                  <span>{formatDuration(liveUploadElapsedMs)}</span>
                  <span>{formatUploadSpeed(currentUploadSpeed)}</span>
                </div>
              </div>
            ) : null}

            <div style={styles.uploadModalTrack}>
              <div
                style={{
                  ...styles.uploadModalFill,
                  ...(showFileUploadDetails ? styles.uploadModalFillDeterminate : null),
                  width: showFileUploadDetails ? `${Math.max(2, currentFilePercent)}%` : "45%",
                }}
              />
            </div>
            <div style={styles.uploadModalFoot}>Files upload one at a time. If an upload stops, this session will close and you can decide when to try again.</div>
          </div>
        </div>
      ) : null}
      <header style={{ ...styles.pageHeader, ...(isSmall ? styles.pageHeaderMobile : null) }}>
        <div><div style={styles.kicker}>Case Analyzer</div><h1 style={{ ...styles.title, ...(isSmall ? styles.titleMobile : null) }}>Analyze probability, evidence, and case risk</h1><p style={styles.subtitle}>Complete a guided intake, organize and upload your evidence, and receive a structured Case Analyzer report with multi-level analysis and quality checks.</p></div>
      </header>

      <div className="case-analyzer-main-grid" style={styles.mainGrid}>
        <main style={styles.mainColumn}>
          <div className="case-analyzer-feature-grid" style={{ ...styles.featureGrid, ...(isSmall ? styles.featureGridMobile : null) }}><FeatureTile icon="%" title="Probability profile" text="Estimate favorable, mixed, and adverse outcome signals." /><FeatureTile icon="▤" title="Evidence map" text="Connect evidence bundles to facts, issues, risks, and gaps." /><FeatureTile icon="◇" title="Saved report" text="Save final reports to File Vault after completion." /></div>

          <section style={styles.workflowPanel}><div style={styles.workflowInner}>
            <SectionHeader number="1" title="Case profile" text="Tell us what kind of case this is and where you stand." open={openSections.profile} onToggle={() => toggleSection("profile")} />
            {openSections.profile && <div>
              <div className="case-analyzer-field-grid" style={styles.fieldGrid}>
                <div id="case-analyzer-field-case_type"><Field label="Case type" required><select className="case-analyzer-select" value={form.case_type} onChange={(e) => updateForm({ case_type: e.target.value, case_subtype: "" })} style={styles.select}><option value="">Select case type</option>{CASE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select>{fieldErrors.case_type && <span style={styles.inlineError}>{fieldErrors.case_type}</span>}</Field></div>
                <div id="case-analyzer-field-case_subtype"><Field label="Case subtype" required><select className="case-analyzer-select" value={form.case_subtype} onChange={(e) => updateForm({ case_subtype: e.target.value })} style={styles.select} disabled={!subtypeOptions.length}><option value="">{subtypeOptions.length ? "Select subtype" : "Select case type first"}</option>{subtypeOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select>{fieldErrors.case_subtype && <span style={styles.inlineError}>{fieldErrors.case_subtype}</span>}</Field></div>
                <div id="case-analyzer-field-user_role"><Field label="Your role" required><select className="case-analyzer-select" value={form.user_role} onChange={(e) => updateForm({ user_role: e.target.value })} style={styles.select}><option value="">Select your role</option>{USER_ROLES.map((v) => <option key={v} value={v}>{v}</option>)}</select>{fieldErrors.user_role && <span style={styles.inlineError}>{fieldErrors.user_role}</span>}</Field></div>
                <Field label="Opposing party type"><select className="case-analyzer-select" value={form.opposing_party_type} onChange={(e) => updateForm({ opposing_party_type: e.target.value })} style={styles.select}><option value="">Select party type</option>{PARTY_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
                <Field label="Do you have an attorney?"><select className="case-analyzer-select" value={form.user_has_attorney} onChange={(e) => updateForm({ user_has_attorney: e.target.value })} style={styles.select}>{YES_NO_UNKNOWN.map((v) => <option key={v} value={v}>{v}</option>)}<option value="Considering">Considering</option><option value="Had attorney before">Had attorney before</option></select></Field>
                <Field label="Other side has attorney?"><select className="case-analyzer-select" value={form.other_side_has_attorney} onChange={(e) => updateForm({ other_side_has_attorney: e.target.value })} style={styles.select}>{YES_NO_UNKNOWN.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
              </div>
            </div>}

            <div style={styles.divider} />
            <SectionHeader number="2" title="Location and case position" text="Jurisdiction, forum, and stage affect deadlines, procedure, and report reliability." open={openSections.position} onToggle={() => toggleSection("position")} />
            {openSections.position && <div>
              <div className="case-analyzer-field-grid" style={styles.fieldGrid}>
                <div id="case-analyzer-field-country"><Field label="Country" required><input className="case-analyzer-input" value={form.country} onChange={(e) => updateForm({ country: e.target.value })} style={styles.input} />{fieldErrors.country && <span style={styles.inlineError}>{fieldErrors.country}</span>}</Field></div>
                <div id="case-analyzer-field-state"><Field label="State / Province" required={form.country === "United States"}><input className="case-analyzer-input" value={form.state} onChange={(e) => updateForm({ state: e.target.value })} placeholder="California" style={styles.input} />{fieldErrors.state && <span style={styles.inlineError}>{fieldErrors.state}</span>}</Field></div>
                <Field label="County"><input className="case-analyzer-input" value={form.county} onChange={(e) => updateForm({ county: e.target.value })} placeholder="Los Angeles County" style={styles.input} /></Field>
                <Field label="City / ZIP"><div style={styles.splitRow}><input className="case-analyzer-input" value={form.city} onChange={(e) => updateForm({ city: e.target.value })} placeholder="City" style={styles.input} /><input className="case-analyzer-input" value={form.postal_code} onChange={(e) => updateForm({ postal_code: e.target.value })} placeholder="ZIP" style={styles.input} /></div></Field>
                <div id="case-analyzer-field-forum"><Field label="Forum" required><select className="case-analyzer-select" value={form.forum} onChange={(e) => updateForm({ forum: e.target.value })} style={styles.select}><option value="">Select forum</option>{FORUMS.map((v) => <option key={v} value={v}>{v}</option>)}</select>{fieldErrors.forum && <span style={styles.inlineError}>{fieldErrors.forum}</span>}</Field></div>
                <Field label="Court or agency name"><input className="case-analyzer-input" value={form.court_or_agency_name} onChange={(e) => updateForm({ court_or_agency_name: e.target.value })} placeholder="Optional" style={styles.input} /></Field>
                <div id="case-analyzer-field-case_stage"><Field label="Case stage" required><select className="case-analyzer-select" value={form.case_stage} onChange={(e) => updateForm({ case_stage: e.target.value })} style={styles.select}><option value="">Select stage</option>{CASE_STAGES.map((v) => <option key={v} value={v}>{v}</option>)}</select>{fieldErrors.case_stage && <span style={styles.inlineError}>{fieldErrors.case_stage}</span>}</Field></div>
                <Field label="Important deadline?"><select className="case-analyzer-select" value={form.deadline_known} onChange={(e) => updateForm({ deadline_known: e.target.value })} style={styles.select}>{YES_NO_UNKNOWN.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
                <Field label="Issue start date"><input className="case-analyzer-input" type="date" value={form.issue_start_date} onChange={(e) => updateForm({ issue_start_date: e.target.value })} style={styles.input} /></Field>
                <Field label="Discovery date"><input className="case-analyzer-input" type="date" value={form.issue_discovery_date} onChange={(e) => updateForm({ issue_discovery_date: e.target.value })} style={styles.input} /></Field>
              </div>
              <Field label="Upcoming hearing, mediation, trial, or deadline"><input className="case-analyzer-input" value={form.upcoming_deadline} onChange={(e) => updateForm({ upcoming_deadline: e.target.value })} placeholder="Optional — include date if known." style={styles.input} /></Field>
            </div>}

            <div style={styles.divider} />
            <SectionHeader number="3" title="Facts, requested relief, and damages" text="Tell us the practical story, what you want, what proof exists, and what the other side is saying." open={openSections.facts} onToggle={() => toggleSection("facts")} />
            {openSections.facts && <div>
              <div id="case-analyzer-field-facts_summary"><Field label="Tell us what happened" required help="Please give us as much detail as possible. A short but clear story helps the report connect facts, evidence, damages, and the other side’s position.">
                <textarea
                  className="case-analyzer-textarea"
                  value={form.facts_summary}
                  onChange={(e) => updateForm({ facts_summary: e.target.value })}
                  placeholder="Include dates, who was involved, what happened, what went wrong, what documents exist, what the other side says, and what result you want. A few clear sentences is enough to start."
                  style={{ ...styles.textarea, ...styles.factsTextarea, ...(factsSummaryMeetsMin ? {} : styles.textareaNeedsInput) }}
                />
                <div style={styles.characterCounterRow}>
                  <span style={factsSummaryMeetsMin ? styles.characterCounterGood : styles.characterCounterBad}>
                    {factsSummaryLength.toLocaleString()} / {FACTS_SUMMARY_MIN_CHARS.toLocaleString()} minimum characters
                  </span>
                </div>
                {fieldErrors.facts_summary && <span style={styles.inlineError}>{fieldErrors.facts_summary}</span>}
              </Field></div>
              <div className="case-analyzer-field-grid" style={styles.fieldGrid}><Field label="Desired result"><input className="case-analyzer-input" value={form.desired_result} onChange={(e) => updateForm({ desired_result: e.target.value })} placeholder="Money, settlement, dismissal, repair, reinstatement, stop conduct, etc." style={styles.input} /></Field><Field label="Estimated amount involved"><input className="case-analyzer-input" value={form.estimated_amount} onChange={(e) => updateForm({ estimated_amount: e.target.value })} placeholder="$50,000" style={styles.input} /></Field></div>
              <Field label="Explain your damages or requested relief"><textarea className="case-analyzer-textarea" value={form.relief_explanation} onChange={(e) => updateForm({ relief_explanation: e.target.value })} placeholder="Explain how the amount was calculated, what losses occurred, what non-money outcome you want, and whether the other side disputes the amount or remedy." style={{ ...styles.textarea, minHeight: 115 }} /></Field>
              <Field label="What has the other side said?"><textarea className="case-analyzer-textarea" value={form.other_side_position} onChange={(e) => updateForm({ other_side_position: e.target.value })} placeholder="Summarize any excuses, defenses, denials, settlement offers, admissions, or threats." style={{ ...styles.textarea, minHeight: 110 }} /></Field>
              <div style={styles.checkPanel}><h3 style={styles.checkTitle}>Correspondence involved</h3><CheckboxGroup values={CORRESPONDENCE_TYPES} selected={form.correspondence_types} onChange={(items) => updateForm({ correspondence_types: items })} columns={isSmall ? 1 : 2} /></div>
              <div style={styles.checkPanel}><h3 style={styles.checkTitle}>Requested relief / damages involved</h3><CheckboxGroup values={RELIEF_TYPES} selected={form.relief_types} onChange={(items) => updateForm({ relief_types: items })} columns={isSmall ? 1 : 2} /></div>
            </div>}

            <div style={styles.divider} />
            <div id="case-analyzer-field-evidence"><SectionHeader number="4" title="Evidence bundles" text="Select evidence types and upload one or more files inside each bundle." open={openSections.evidence} onToggle={() => toggleSection("evidence")} />
            {openSections.evidence && <div>
              <div style={styles.evidenceInstruction}>Pick the evidence groups that match your case. Use Other evidence / not sure only when none of the listed groups fit. After you click Add, a bundle card opens below where you can explain what the evidence proves and upload files. Please do not refresh the page while uploads or analysis are running.</div>
              {evidenceError && <div style={{ ...styles.notice, ...styles.noticeError }}>{evidenceError}</div>}
              <BundlePicker key={`primary-${bundlePickerResetKey}`} bundles={primaryEvidenceBundles} activeTemplateIds={activeTemplateIds} onAdd={(template) => addBundle(template, "universal")} />
              <div style={styles.showMoreEvidenceWrap}>
                <button type="button" style={styles.showMoreEvidenceButton} onClick={() => setShowMoreEvidenceBundles((prev) => !prev)}>
                  {showMoreEvidenceBundles ? "Hide additional evidence bundles" : "Show more evidence bundles"}
                </button>
              </div>
              {showMoreEvidenceBundles ? <BundlePicker key={`additional-${bundlePickerResetKey}`} bundles={additionalEvidenceBundles} activeTemplateIds={activeTemplateIds} onAdd={(template) => addBundle(template, "universal")} /> : null}
              <div id="selected-evidence-bundles" style={styles.activeBundlesWrap}>
                {evidenceBundles.length ? evidenceBundles.map((bundle) => <EvidenceBundleCard key={bundle.bundle_id} bundle={bundle} disabled={busy} uploadLocked={uploadLocked || packageAddLocked} onUpdate={updateBundle} onRemove={removeBundle} onAddFiles={addFilesToBundle} onRemoveFile={removeFileFromBundle} onManualPageCountChange={updateManualPageCount} />) : <div style={styles.emptyEvidenceState}>Choose an evidence bundle above to begin. You can add multiple files under the same bundle, such as several email PDFs or multiple invoices.</div>}
              </div>
            </div>}</div>

            <div style={styles.finalRunBox}>
              <CaseAnalyzerPackageCard
                packageInfo={caseAnalyzerPackageInfo}
                stats={evidenceStats}
                selectedPackage={selectedPackage}
                selectedPackageTier={selectedPackagePageLimit}
                selectedFileLimit={selectedPackageFileLimit}
                selectedPackageLimitError={selectedPackageLimitError}
                recommendedLowerPackage={recommendedLowerPackage}
                higherPackageConfirmationRequired={higherPackageConfirmationRequired}
                higherPackageConfirmed={Boolean(recommendedLowerPackage && !higherPackageConfirmationRequired)}
                loading={billingLoading}
                error={billingError}
                onRefresh={() => fetchBillingSnapshot()}
                onBilling={() => navigate("/billing")}
                onSelectPackage={selectPackageTier}
                onUseRecommendedPackage={useRecommendedLowerPackage}
                onConfirmSelectedPackage={confirmSelectedHigherPackage}
                expanded={casePackageOpen}
                onToggle={() => setCasePackageOpen((prev) => !prev)}
              />
              <EvidencePackageMeter
                stats={evidenceStats}
                selectedPackage={selectedPackage}
                selectedFileLimit={selectedPackageFileLimit}
                locked={uploadLocked || packageUploadLocked || manualPageCountLocked}
                packageError={manualPageCountError || selectedPackageLimitError}
              />
              <div id="case-analyzer-field-accepted_terms"><label style={styles.termsLabel} className="case-analyzer-linkish"><input type="checkbox" checked={form.accepted_terms} onChange={(e) => updateForm({ accepted_terms: e.target.checked })} style={styles.termsCheckbox} /><span>I agree to the <a href="/terms">Terms of Use</a> and <a href="/privacy">Privacy Policy</a>.</span></label>{fieldErrors.accepted_terms && <div style={styles.inlineError}>{fieldErrors.accepted_terms}</div>}</div>
              {notice && <div style={{ ...styles.notice, ...(notice.type === "ok" ? styles.noticeOk : styles.noticeError) }}>{notice.msg}</div>}
              {busy && <div style={styles.progressWrap}><div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${Math.max(5, Math.min(100, progress || 8))}%` }} /></div><div style={styles.progressText}>{job?.message || job?.status || "Processing case documents and preparing report…"}</div></div>}
              <button type="button" style={{ ...styles.analyzeButton, ...(analysisDisabled ? styles.buttonDisabled : null) }} onClick={handleAnalyze} disabled={analysisDisabled}>{busy ? <span style={styles.buttonSpinner} aria-hidden="true" /> : <span style={styles.buttonSpark}>✦</span>}{analyzeButtonLabel}</button>
            </div>
          </div></section>
        </main>
        <aside style={styles.sideColumn}>
          <section style={styles.sidePanel}><div style={styles.sideTitle}>How Case Analyzer works</div><MiniStep number="1" title="Complete intake" text="Case type, subtype, role, location, stage, facts, and requested outcome." /><MiniStep number="2" title="Bundle evidence" text="Group files by what they prove, not just by filename." /><MiniStep number="3" title="Generate report" text="Your evidence is extracted, mapped, scored, and returned in a structured report." last /></section>
          <section style={styles.sidePanel}><div style={styles.sideTitle}>Evidence package limits</div><p style={styles.sideCopy}>Select one of your available packages before analysis. The selected package controls both the page limit and file limit. Each file can be up to 200 MB, and the full evidence package can be up to 750 MB.</p><button type="button" style={styles.sideButton} onClick={() => navigate("/billing")}>Manage packages <span aria-hidden>›</span></button></section>
        </aside>
      </div>
    </div>
  );
}

function CaseAnalyzerPackageCard({
  packageInfo,
  stats,
  selectedPackage,
  selectedPackageTier,
  selectedFileLimit,
  selectedPackageLimitError,
  recommendedLowerPackage,
  higherPackageConfirmationRequired,
  higherPackageConfirmed,
  loading,
  error,
  onRefresh,
  onBilling,
  onSelectPackage,
  onUseRecommendedPackage,
  onConfirmSelectedPackage,
  expanded = true,
  onToggle,
}) {
  const available = packageInfo?.availablePackages || [];
  const declaredPages = Number(stats?.declaredTotalPageCount || stats?.totalPageCount || 0);
  const detectedPages = Number(stats?.detectedPageCount || 0);
  const userEnteredPages = Number(stats?.userEnteredPageCount || 0);
  const fileCount = Number(stats?.count || 0);
  const missingManual = Number(stats?.missingManualPageCountFiles || 0);
  const recommendedTier = Number(recommendedLowerPackage?.page_tier || 0);
  const recommendedFileLimit = getCaseAnalyzerFileLimit(recommendedTier);

  return <div style={styles.casePackageCard}>
    <div className="case-analyzer-package-top" style={styles.casePackageTop}>
      <button type="button" style={styles.casePackageHeaderButton} onClick={onToggle}>
        <span style={styles.casePackageToggle}>{expanded ? "−" : "+"}</span>
        <span>
          <span style={styles.casePackageTitle}>Select your Case Analyzer package</span>
          <span style={styles.casePackageSub}>The lowest available package is selected by default. You may select another package you own.</span>
        </span>
      </button>
      <button type="button" style={styles.casePackageRefreshButton} onClick={onRefresh} disabled={loading}>{loading ? "Checking…" : "Refresh"}</button>
    </div>

    {expanded ? <>
      {error ? <div style={styles.casePackageError}>{error}</div> : null}

      {!loading && !available.length ? <div style={styles.casePackageEmpty}>
        No Case Analyzer package is currently available on this account.
        <button type="button" style={styles.casePackageBillingButton} onClick={onBilling}>Manage packages</button>
      </div> : null}

      {available.length ? <div style={styles.casePackageList} role="radiogroup" aria-label="Available Case Analyzer packages">
        {available.map((row) => {
          const tier = Number(row.page_tier || 0);
          const fileLimit = getCaseAnalyzerFileLimit(tier);
          const selected = tier === Number(selectedPackageTier || 0);
          return <label
            key={tier}
            style={{
              ...styles.casePackageRow,
              ...(selected ? styles.casePackageRowSelected : null),
            }}
          >
            <input
              type="radio"
              name="case-analyzer-package"
              value={tier}
              checked={selected}
              onChange={() => onSelectPackage(tier)}
              style={styles.casePackageRadio}
            />
            <span style={styles.casePackageRowMain}>
              <span style={styles.casePackageRowTitle}>{tier.toLocaleString()}-page package</span>
              <span style={styles.casePackageRowMeta}>Up to {tier.toLocaleString()} pages • Up to {fileLimit.toLocaleString()} files</span>
            </span>
            <strong style={styles.casePackageRowRemaining}>{Number(row.remaining || 0)} report{Number(row.remaining || 0) === 1 ? "" : "s"} remaining</strong>
          </label>;
        })}
      </div> : null}

      <div style={{
        ...styles.casePackageCurrent,
        ...(selectedPackageLimitError ? styles.casePackageCurrentBad : selectedPackage ? styles.casePackageCurrentGood : null),
      }}>
        <div><strong>Selected package:</strong> {selectedPackage ? `${Number(selectedPackageTier || 0).toLocaleString()} pages • ${Number(selectedFileLimit || 0).toLocaleString()} files` : "None selected"}</div>
        <div style={styles.casePackageBreakdown}><strong>Current evidence:</strong> {declaredPages.toLocaleString()} pages • {fileCount.toLocaleString()} files</div>
        <div style={styles.casePackageBreakdown}>{detectedPages.toLocaleString()} detected page{detectedPages === 1 ? "" : "s"} + {userEnteredPages.toLocaleString()} user-entered page{userEnteredPages === 1 ? "" : "s"}</div>
        {missingManual ? <div style={styles.casePackageManualWarning}>Enter page counts for {missingManual} file{missingManual === 1 ? "" : "s"} before starting analysis.</div> : null}
        {selectedPackageLimitError ? <div style={styles.casePackageLimitMessage}>{selectedPackageLimitError}</div> : null}
      </div>

      {recommendedLowerPackage ? <div style={styles.casePackageRecommendation}>
        <div style={styles.casePackageRecommendationTitle}>A lower package you own also fits this evidence.</div>
        <div style={styles.casePackageRecommendationText}>
          Your evidence fits your available {recommendedTier.toLocaleString()}-page package, which allows up to {recommendedFileLimit.toLocaleString()} files.
          {higherPackageConfirmationRequired
            ? ` Select that package, or confirm that you want to use the selected ${Number(selectedPackageTier || 0).toLocaleString()}-page package.`
            : ` You confirmed that you want to use the selected ${Number(selectedPackageTier || 0).toLocaleString()}-page package.`}
        </div>
        {higherPackageConfirmationRequired ? <div style={styles.casePackageRecommendationActions}>
          <button type="button" style={styles.casePackageUseLowerButton} onClick={onUseRecommendedPackage}>Use {recommendedTier.toLocaleString()}-page package</button>
          <button type="button" style={styles.casePackageUseSelectedButton} onClick={onConfirmSelectedPackage}>Use {Number(selectedPackageTier || 0).toLocaleString()}-page package anyway</button>
        </div> : <div style={styles.casePackageConfirmed}>Larger package selection confirmed.</div>}
      </div> : null}
    </> : null}
  </div>;
}

function EvidencePackageMeter({ stats, selectedPackage, selectedFileLimit, locked, packageError }) {
  const bytePercent = Math.min(100, Math.max(0, stats.percent || 0));
  const selectedPageLimit = Number(selectedPackage?.page_tier || 0);
  const pagePercent = selectedPageLimit > 0
    ? Math.min(100, Math.round((Number(stats.totalPageCount || 0) / selectedPageLimit) * 100))
    : 0;
  const filePercent = Number(selectedFileLimit || 0) > 0
    ? Math.min(100, Math.round((Number(stats.count || 0) / Number(selectedFileLimit || 1)) * 100))
    : 0;
  const overallPercent = Math.max(bytePercent, pagePercent, filePercent);
  const fillStyle = overallPercent >= 90 ? styles.packageFillDanger : overallPercent >= 70 ? styles.packageFillWarn : styles.packageFillOk;
  const detectedPagesText = `${stats.detectedPageCount || 0} detected page${Number(stats.detectedPageCount || 0) === 1 ? "" : "s"}`;
  const userEnteredPagesText = `${stats.userEnteredPageCount || 0} user-entered page${Number(stats.userEnteredPageCount || 0) === 1 ? "" : "s"}`;
  const declaredPagesText = selectedPageLimit > 0
    ? `${stats.totalPageCount || 0} / ${selectedPageLimit.toLocaleString()} declared pages`
    : `${stats.totalPageCount || 0} declared pages`;
  const fileCountText = Number(selectedFileLimit || 0) > 0
    ? `${stats.count || 0} / ${Number(selectedFileLimit || 0).toLocaleString()} files`
    : `${stats.count || 0} files`;
  const missingText = stats.missingManualPageCountFiles ? ` • ${stats.missingManualPageCountFiles} required page count${stats.missingManualPageCountFiles === 1 ? "" : "s"} missing` : "";
  const remainingPages = selectedPageLimit > 0 ? Math.max(0, selectedPageLimit - Number(stats.totalPageCount || 0)) : 0;
  const remainingFiles = Number(selectedFileLimit || 0) > 0 ? Math.max(0, Number(selectedFileLimit || 0) - Number(stats.count || 0)) : 0;

  return <div style={styles.packageMeterCard}>
    <div style={styles.packageMeterTop}>
      <div>
        <div style={styles.packageMeterTitle}>Evidence package</div>
        <div style={styles.packageMeterSub}>{formatBytes(stats.totalBytes)} / {MAX_TOTAL_UPLOAD_MB} MB selected • {fileCountText}</div>
        <div style={styles.packageMeterSub}>{declaredPagesText} • {detectedPagesText} + {userEnteredPagesText}{missingText}</div>
      </div>
      <div style={{ ...styles.packagePercent, ...(overallPercent >= 90 ? styles.packagePercentDanger : overallPercent >= 70 ? styles.packagePercentWarn : null) }}>{overallPercent}%</div>
    </div>
    <div style={styles.packageTrack}><div style={{ ...styles.packageFill, ...fillStyle, width: `${overallPercent}%` }} /></div>
    {locked
      ? <div style={styles.packageLimitText}>{packageError || "You reached one of the selected package limits. Remove lower-priority files before adding more evidence."}</div>
      : selectedPackage
        ? <div style={styles.packageHelpText}>Remaining room under the selected package: {remainingPages.toLocaleString()} page(s), {remainingFiles.toLocaleString()} file(s), and {formatBytes(stats.remainingBytes)} of upload space.</div>
        : <div style={styles.packageLimitText}>Select one of your available packages before adding evidence.</div>}
  </div>;
}

function BundlePicker({ title, bundles, activeTemplateIds, onAdd }) {
  return <div style={styles.bundlePickerPanel}>{title ? <div style={styles.bundlePickerTitle}>{title}</div> : null}<div className="case-analyzer-bundle-picker-grid" style={styles.bundlePickerGrid}>{bundles.map((bundle) => {
    const active = activeTemplateIds.has(bundle.id);
    return <button
      key={`${bundle.id}-${active ? "active" : "neutral"}`}
      type="button"
      className={`case-analyzer-bundle-pick-card ${active ? "is-added-bundle" : "is-neutral-bundle"}`}
      style={{ ...styles.bundlePickCard, ...(active ? styles.bundlePickCardActive : null) }}
      onMouseDown={(e) => e.preventDefault()}
      onBlur={clearBundleCardFocusFootprint}
      onClick={() => {
        clearBundleCardFocusFootprint();
        onAdd(bundle);
        window.setTimeout(clearBundleCardFocusFootprint, 0);
      }}
      disabled={active}
    >
      <span style={styles.bundlePickCheck}>{active ? "✓" : "+"}</span><span><span style={styles.bundlePickTitle}>{bundle.label}</span><span style={styles.bundlePickHint}>{bundle.hint}</span><span style={styles.bundlePickAction}>{active ? "Added" : "Add bundle"}</span></span>
    </button>;
  })}</div></div>;
}

function EvidenceBundleCard({ bundle, disabled, uploadLocked, onUpdate, onRemove, onAddFiles, onRemoveFile, onManualPageCountChange }) {
  const fileCount = (bundle.files || []).length;
  const hasFiles = fileCount > 0;
  const cardStateStyle = hasFiles ? styles.evidenceBundleCardComplete : styles.evidenceBundleCardNeedsFiles;
  const fileBadgeStateStyle = hasFiles ? styles.fileCountBadgeComplete : styles.fileCountBadgeEmpty;

  return <div style={{ ...styles.evidenceBundleCard, ...cardStateStyle }}>
    <div style={styles.evidenceBundleHeader}>
      <button type="button" style={styles.bundleCollapseButton} onMouseDown={(e) => e.preventDefault()} onClick={() => onUpdate(bundle.bundle_id, { expanded: !bundle.expanded })}>{bundle.expanded ? "−" : "+"}</button>
      <div style={styles.evidenceBundleHeaderText}><div style={styles.evidenceBundleTitle}>{bundle.category}</div><div style={styles.evidenceBundleHint}>{bundle.hint || "Custom evidence bundle."}</div></div>
      <div style={styles.bundleHeaderRight}><div style={{ ...styles.fileCountBadge, ...fileBadgeStateStyle }}>{fileCount} file{fileCount === 1 ? "" : "s"}</div><button type="button" style={styles.removeBundleButton} onMouseDown={(e) => e.preventDefault()} onClick={() => onRemove(bundle.bundle_id)} disabled={disabled}>Remove</button></div>
    </div>
    {bundle.expanded ? <div style={styles.evidenceBundleBody}>
      <Field label="What does this evidence show?"><textarea className="case-analyzer-textarea" value={bundle.what_it_proves || ""} onChange={(e) => onUpdate(bundle.bundle_id, { what_it_proves: e.target.value })} placeholder="Example: These emails show the other side admitted the work was completed and promised payment." style={{ ...styles.textarea, minHeight: 92 }} /></Field>
      <div className="case-analyzer-field-grid" style={styles.fieldGrid}>
        <Field label="Supports"><select className="case-analyzer-select" value={bundle.supports_side || "Not sure"} onChange={(e) => onUpdate(bundle.bundle_id, { supports_side: e.target.value })} style={styles.select}>{SUPPORTS_SIDE.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
        <Field label="Importance"><select className="case-analyzer-select" value={bundle.importance || "Not sure"} onChange={(e) => onUpdate(bundle.bundle_id, { importance: e.target.value })} style={styles.select}>{IMPORTANCE_LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
        <Field label="Evidence quality"><select className="case-analyzer-select" value={bundle.evidence_quality || "Not sure"} onChange={(e) => onUpdate(bundle.bundle_id, { evidence_quality: e.target.value })} style={styles.select}>{EVIDENCE_QUALITY_LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
        <Field label="Date range"><input className="case-analyzer-input" value={bundle.date_range || ""} onChange={(e) => onUpdate(bundle.bundle_id, { date_range: e.target.value })} placeholder="Example: Jan 2025 – Mar 2025" style={styles.input} /></Field>
      </div>
      <div style={styles.bundleFileBox}>
        <div className="case-analyzer-bundle-file-top" style={styles.bundleFileTop}><div><div style={styles.bundleFileTitle}>Files in this bundle</div><div style={styles.bundleFileSub}>Add all files that belong to this evidence category.</div></div><label style={{ ...styles.fileAddButton, ...(disabled || uploadLocked ? styles.fileAddButtonDisabled : null) }}>Add file<input type="file" multiple disabled={disabled || uploadLocked} onChange={(e) => { onAddFiles(bundle.bundle_id, e.target.files); e.target.value = ""; }} style={{ display: "none" }} /></label></div>
        {(bundle.files || []).length ? <div style={styles.fileList}>{bundle.files.map((item) => {
          const needsManual = itemNeedsManualPageCount(item);
          const pageCount = getItemPageCount(item);
          const manualValue = item.manual_page_count_input ?? item.manualPageCountInput ?? (needsManual && pageCount ? String(pageCount) : "");
          return <div key={item.client_id} className="case-analyzer-file-row" style={styles.fileRow}>
            <div style={styles.fileName}>
              {item.file?.name || "Evidence file"}
              <div style={styles.fileMetaText}>{getItemPageCountDisplay(item)} • {formatBytes(item.file?.size || 0)}</div>
              {needsManual ? <div style={styles.manualPageBox}>
                <label style={styles.manualPageLabel}>Page count required</label>
                <input
                  className="case-analyzer-input"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={manualValue}
                  onChange={(e) => onManualPageCountChange(bundle.bundle_id, item.client_id, e.target.value)}
                  placeholder="Enter pages"
                  style={styles.manualPageInput}
                  disabled={disabled}
                />
              </div> : null}
            </div>
            <div style={styles.fileSize}>{formatBytes(item.file?.size || 0)}</div>
            <button type="button" style={styles.fileRemoveButton} onMouseDown={preventBundleFocusFootprint} onClick={() => onRemoveFile(bundle.bundle_id, item.client_id)} disabled={disabled}>Remove</button>
          </div>;
        })}</div> : <div style={styles.noFilesText}>No files added yet. Add one or more files that belong to this evidence bundle.</div>}
      </div>
    </div> : null}
  </div>;
}

function FeatureTile({ icon, title, text }) { return <div style={styles.featureTile}><div style={styles.featureIcon}>{icon}</div><div><div style={styles.featureTitle}>{title}</div><div style={styles.featureText}>{text}</div></div></div>; }
function MiniStep({ number, title, text, last = false }) { return <div style={styles.miniStep}><div style={styles.miniStepLeft}><div style={styles.miniStepNumber}>{number}</div>{!last && <div style={styles.miniStepLine} />}</div><div style={styles.miniStepBody}><div style={styles.miniStepTitle}>{title}</div><div style={styles.miniStepText}>{text}</div></div></div>; }

const styles = {
  outerWrap: { width: "100%", maxWidth: 1120, minWidth: 0, minHeight: "100vh", margin: "0 auto", padding: "18px 16px 44px", boxSizing: "border-box", overflowX: "hidden", background: "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF", fontFamily: "sans-serif" }, outerWrapMobile: { padding: "14px 12px 34px" },
  pageHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 18, marginBottom: 18, paddingTop: 12 }, pageHeaderMobile: { display: "block", textAlign: "center", paddingTop: 12 },
  kicker: { color: "#2563EB", fontSize: 13, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 },
  title: { margin: 0, color: "#0F172A", fontSize: 30, lineHeight: 1.08, fontWeight: 950, letterSpacing: "-1px", maxWidth: 760, overflowWrap: "break-word", wordBreak: "normal" }, titleMobile: { fontSize: 28, lineHeight: 1.18, textAlign: "center", overflowWrap: "break-word", wordBreak: "normal" },
  subtitle: { margin: "9px 0 0", color: "#475569", fontSize: 15, lineHeight: 1.55, maxWidth: 780, fontWeight: 650 },
  mainGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 310px", gap: 16, alignItems: "start" }, mainColumn: { minWidth: 0 }, sideColumn: { minWidth: 0, display: "grid", gridTemplateColumns: "1fr", gap: 14 },
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 14 }, featureGridMobile: { gridTemplateColumns: "1fr" },
  featureTile: { display: "flex", alignItems: "center", gap: 12, minHeight: 80, padding: 14, borderRadius: 16, border: "1px solid rgba(37,99,235,0.13)", background: "linear-gradient(180deg,#FFFFFF,#F8FBFF)", boxShadow: "0 10px 24px rgba(2,6,23,0.04)" }, featureIcon: { width: 36, height: 36, borderRadius: 14, display: "grid", placeItems: "center", background: "#EFF6FF", color: "#2563EB", fontWeight: 950, fontSize: 18 }, featureTitle: { color: "#0F172A", fontWeight: 950, fontSize: 14 }, featureText: { color: "#64748B", fontWeight: 650, fontSize: 12, lineHeight: 1.45, marginTop: 3 },
  workflowPanel: { borderRadius: 22, border: "1px solid rgba(15,23,42,0.08)", background: "#FFFFFF", boxShadow: "0 18px 45px rgba(15,23,42,0.08)", overflow: "hidden" }, workflowInner: { padding: 16 },
  sectionHeaderButton: { width: "100%", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }, sectionHeader: { display: "flex", alignItems: "center", gap: 13, padding: "9px 0" }, stepBadge: { width: 34, height: 34, borderRadius: 13, background: "#2563EB", color: "#FFFFFF", display: "grid", placeItems: "center", fontWeight: 950, flex: "0 0 auto" }, sectionHeaderText: { minWidth: 0, flex: 1 }, sectionTitle: { margin: 0, color: "#0F172A", fontSize: 20, fontWeight: 950, letterSpacing: "-0.3px" }, sectionText: { margin: "3px 0 0", color: "#64748B", fontSize: 13, lineHeight: 1.45, fontWeight: 650 }, sectionChevron: { width: 32, height: 32, borderRadius: 12, background: "#F1F5F9", color: "#0F172A", display: "grid", placeItems: "center", fontWeight: 950, fontSize: 20 }, divider: { height: 1, background: "rgba(15,23,42,0.08)", margin: "14px 0" },
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }, field: { display: "block", margin: "10px 0" }, fieldLabel: { display: "block", color: "#0F172A", fontWeight: 900, fontSize: 13, marginBottom: 6 }, required: { color: "#DC2626" }, helpText: { display: "block", marginTop: 6, color: "#64748B", fontWeight: 650, fontSize: 12, lineHeight: 1.4 }, input: { width: "100%", boxSizing: "border-box", border: "1px solid rgba(15,23,42,0.14)", borderRadius: 13, padding: "12px 12px", fontSize: 14, fontWeight: 700, color: "#0F172A", background: "#FFFFFF" }, select: { width: "100%", boxSizing: "border-box", border: "1px solid rgba(15,23,42,0.14)", borderRadius: 13, padding: "12px 12px", fontSize: 14, fontWeight: 700, color: "#0F172A", background: "#FFFFFF" }, textarea: { width: "100%", minHeight: 150, maxHeight: 360, boxSizing: "border-box", border: "1px solid rgba(15,23,42,0.14)", borderRadius: 14, padding: 13, fontSize: 14, lineHeight: 1.55, fontWeight: 650, color: "#0F172A", background: "#FFFFFF", resize: "vertical", overflowY: "auto" }, factsTextarea: { minHeight: 170 }, textareaNeedsInput: { borderColor: "rgba(220,38,38,0.42)", boxShadow: "0 0 0 3px rgba(220,38,38,0.08)" }, characterCounterRow: { display: "flex", justifyContent: "flex-end", marginTop: 6 }, characterCounterBad: { color: "#DC2626", fontWeight: 900, fontSize: 12 }, characterCounterGood: { color: "#047857", fontWeight: 900, fontSize: 12 }, splitRow: { display: "grid", gridTemplateColumns: "1fr 0.7fr", gap: 8 }, inlineError: { display: "block", color: "#DC2626", fontWeight: 800, fontSize: 12, marginTop: 6 },
  checkPanel: { marginTop: 12, padding: 13, borderRadius: 16, background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.08)" }, checkTitle: { margin: "0 0 10px", color: "#0F172A", fontSize: 14, fontWeight: 950 }, checkboxGrid: { display: "grid", gap: 8 }, checkboxLabel: { display: "flex", alignItems: "flex-start", gap: 8, color: "#334155", fontSize: 13, fontWeight: 700, lineHeight: 1.35 }, checkbox: { marginTop: 2 },
  evidenceInstruction: { padding: 13, borderRadius: 16, background: "#EFF6FF", color: "#1E3A8A", border: "1px solid rgba(37,99,235,0.16)", fontWeight: 750, fontSize: 13, lineHeight: 1.45, marginBottom: 12 },
  bundlePickerPanel: { marginTop: 12, borderRadius: 18, border: "1px solid rgba(15,23,42,0.08)", background: "#FFFFFF", padding: 12 }, bundlePickerTitle: { fontWeight: 950, color: "#0F172A", marginBottom: 10 }, bundlePickerGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9 }, bundlePickCard: { display: "flex", alignItems: "flex-start", gap: 9, padding: 11, borderRadius: 14, border: "1px solid rgba(15,23,42,0.10)", background: "#F8FAFC", textAlign: "left", cursor: "pointer", outline: "none", boxShadow: "none", WebkitTapHighlightColor: "transparent", appearance: "none", WebkitAppearance: "none" }, bundlePickCardActive: { background: "#ECFDF5", borderColor: "rgba(5,150,105,0.30)", cursor: "default" }, bundlePickCheck: { width: 22, height: 22, borderRadius: 9, background: "#2563EB", color: "#FFFFFF", display: "grid", placeItems: "center", fontWeight: 950, flex: "0 0 auto" }, bundlePickTitle: { display: "block", color: "#0F172A", fontWeight: 950, fontSize: 13 }, bundlePickHint: { display: "block", color: "#64748B", fontWeight: 650, fontSize: 12, lineHeight: 1.35, marginTop: 3 }, bundlePickAction: { display: "block", color: "#2563EB", fontWeight: 950, fontSize: 12, marginTop: 6 }, suggestionEmpty: { marginTop: 12, padding: 12, borderRadius: 16, background: "#F8FAFC", color: "#64748B", fontWeight: 700, fontSize: 13 },
  showMoreEvidenceWrap: { display: "flex", justifyContent: "center", margin: "14px 0 10px" },
  showMoreEvidenceButton: { border: "1px solid rgba(37,99,235,0.24)", background: "#FFFFFF", color: "#1D4ED8", borderRadius: 999, padding: "10px 16px", fontWeight: 950, cursor: "pointer", boxShadow: "0 8px 18px rgba(37,99,235,0.10)" },
   activeBundlesWrap: { marginTop: 12, display: "grid", gap: 12 }, emptyEvidenceState: { padding: 16, borderRadius: 16, background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.08)", color: "#64748B", fontWeight: 700, lineHeight: 1.45 },
  evidenceBundleCard: { borderRadius: 18, border: "1px solid rgba(15,23,42,0.10)", background: "#FFFFFF", overflow: "hidden", transition: "border-color 0.18s ease, box-shadow 0.18s ease" }, evidenceBundleCardNeedsFiles: { borderColor: "rgba(220,38,38,0.62)", boxShadow: "0 0 0 3px rgba(220,38,38,0.08)" }, evidenceBundleCardComplete: { borderColor: "rgba(5,150,105,0.62)", boxShadow: "0 0 0 3px rgba(5,150,105,0.08)" }, evidenceBundleHeader: { display: "flex", alignItems: "center", gap: 10, padding: 12, background: "#F8FAFC" }, bundleCollapseButton: { width: 30, height: 30, borderRadius: 11, border: "1px solid rgba(15,23,42,0.10)", background: "#FFFFFF", fontWeight: 950, cursor: "pointer", outline: "none" }, evidenceBundleHeaderText: { flex: 1, minWidth: 0 }, evidenceBundleTitle: { color: "#0F172A", fontWeight: 950, fontSize: 14 }, evidenceBundleHint: { color: "#64748B", fontWeight: 650, fontSize: 12, lineHeight: 1.35, marginTop: 2 }, bundleHeaderRight: { display: "flex", alignItems: "center", gap: 8 }, fileCountBadge: { padding: "6px 9px", borderRadius: 999, background: "#E0F2FE", color: "#075985", fontWeight: 950, fontSize: 12 }, fileCountBadgeEmpty: { background: "#FEE2E2", color: "#991B1B" }, fileCountBadgeComplete: { background: "#D1FAE5", color: "#065F46" }, removeBundleButton: { border: "none", background: "#FEE2E2", color: "#991B1B", borderRadius: 10, padding: "7px 9px", fontWeight: 900, cursor: "pointer", outline: "none" }, evidenceBundleBody: { padding: 12 }, bundleFileBox: { marginTop: 12, borderRadius: 16, border: "1px solid rgba(15,23,42,0.08)", background: "#F8FAFC", padding: 12 }, bundleFileTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }, bundleFileTitle: { color: "#0F172A", fontWeight: 950, fontSize: 14 }, bundleFileSub: { color: "#64748B", fontWeight: 650, fontSize: 12, marginTop: 2 }, fileAddButton: { borderRadius: 13, background: "#0F172A", color: "#FFFFFF", padding: "10px 13px", fontWeight: 950, cursor: "pointer", whiteSpace: "nowrap" }, fileAddButtonDisabled: { opacity: 0.5, pointerEvents: "none" }, fileList: { marginTop: 10, display: "grid", gap: 8 }, fileRow: { display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center", padding: 9, borderRadius: 13, background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.07)" }, fileName: { minWidth: 0, color: "#0F172A", fontWeight: 850, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, fileMetaText: { color: "#64748B", fontWeight: 700, fontSize: 11, marginTop: 3 }, manualPageBox: { marginTop: 8, display: "grid", gridTemplateColumns: "auto minmax(110px, 160px)", gap: 8, alignItems: "center" }, manualPageLabel: { color: "#B45309", fontWeight: 900, fontSize: 11 }, manualPageInput: { width: "100%", boxSizing: "border-box", border: "1px solid rgba(180,83,9,0.35)", borderRadius: 11, padding: "8px 9px", fontSize: 13, fontWeight: 850, color: "#0F172A", background: "#FFFBEB" }, fileSize: { color: "#64748B", fontWeight: 800, fontSize: 12 }, fileRemoveButton: { border: "none", background: "#F1F5F9", color: "#334155", borderRadius: 10, padding: "7px 9px", fontWeight: 900, cursor: "pointer" }, noFilesText: { marginTop: 10, color: "#64748B", fontSize: 13, fontWeight: 700 },

  casePackageCard: { borderRadius: 16, background: "#EFF6FF", border: "1px solid rgba(37,99,235,0.16)", padding: 12, marginBottom: 12 },
  casePackageTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 },
  casePackageHeaderButton: { display: "flex", alignItems: "flex-start", gap: 9, border: "none", background: "transparent", padding: 0, textAlign: "left", cursor: "pointer", flex: 1 },
  casePackageToggle: { width: 24, height: 24, borderRadius: 9, background: "#FFFFFF", color: "#1D4ED8", display: "grid", placeItems: "center", fontWeight: 950, border: "1px solid rgba(37,99,235,0.22)", flex: "0 0 auto" },
  casePackageTitle: { display: "block", color: "#0F172A", fontWeight: 950, fontSize: 14 },
  casePackageSub: { display: "block", color: "#1E40AF", fontWeight: 700, fontSize: 12, marginTop: 3, lineHeight: 1.45 },
  casePackageRefreshButton: { border: "1px solid rgba(37,99,235,0.22)", background: "#FFFFFF", color: "#1D4ED8", borderRadius: 999, padding: "7px 10px", fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap" },
  casePackageError: { marginTop: 10, color: "#991B1B", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.18)", borderRadius: 12, padding: 10, fontSize: 12, fontWeight: 800, lineHeight: 1.4 },
  casePackageEmpty: { marginTop: 10, color: "#334155", background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", borderRadius: 13, padding: 10, fontSize: 12, fontWeight: 750, lineHeight: 1.4 },
  casePackageBillingButton: { marginTop: 9, width: "100%", border: "none", borderRadius: 12, background: "#0F172A", color: "#FFFFFF", padding: "9px 10px", fontWeight: 950, cursor: "pointer" },
  casePackageList: { marginTop: 10, display: "grid", gap: 8 },
  casePackageRow: { display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", alignItems: "center", gap: 10, borderRadius: 13, background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", padding: "11px 12px", color: "#0F172A", cursor: "pointer" },
  casePackageRowSelected: { borderColor: "rgba(37,99,235,0.52)", boxShadow: "0 0 0 3px rgba(37,99,235,0.10)", background: "#F8FBFF" },
  casePackageRadio: { width: 17, height: 17, accentColor: "#2563EB", cursor: "pointer" },
  casePackageRowMain: { minWidth: 0, display: "grid", gap: 3 },
  casePackageRowTitle: { color: "#0F172A", fontSize: 13, fontWeight: 950 },
  casePackageRowMeta: { color: "#64748B", fontSize: 11, fontWeight: 750, lineHeight: 1.35 },
  casePackageRowRemaining: { color: "#1D4ED8", fontSize: 11, fontWeight: 950, textAlign: "right", whiteSpace: "nowrap" },
  casePackageCurrent: { marginTop: 10, borderRadius: 13, padding: 10, background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.08)", color: "#334155", fontSize: 12, fontWeight: 750, lineHeight: 1.45 },
  casePackageBreakdown: { marginTop: 3, color: "#475569", fontWeight: 800 },
  casePackageManualWarning: { marginTop: 5, color: "#B45309", fontWeight: 900 },
  casePackageLimitMessage: { marginTop: 7, color: "#991B1B", fontWeight: 900 },
  casePackageCurrentGood: { background: "#ECFDF5", color: "#065F46", borderColor: "rgba(5,150,105,0.22)" },
  casePackageCurrentBad: { background: "#FEF2F2", color: "#991B1B", borderColor: "rgba(220,38,38,0.20)" },
  casePackageRecommendation: { marginTop: 10, borderRadius: 13, padding: 11, background: "#FFFBEB", border: "1px solid rgba(217,119,6,0.24)", color: "#78350F" },
  casePackageRecommendationTitle: { fontSize: 12, fontWeight: 950 },
  casePackageRecommendationText: { marginTop: 4, fontSize: 12, fontWeight: 750, lineHeight: 1.45 },
  casePackageRecommendationActions: { marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8 },
  casePackageUseLowerButton: { border: "none", borderRadius: 11, background: "#D97706", color: "#FFFFFF", padding: "8px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer" },
  casePackageUseSelectedButton: { border: "1px solid rgba(146,64,14,0.30)", borderRadius: 11, background: "#FFFFFF", color: "#92400E", padding: "8px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer" },
  casePackageConfirmed: { marginTop: 7, color: "#047857", fontSize: 11, fontWeight: 950 },
  finalRunBox: { marginTop: 20, borderRadius: 18, border: "1px solid rgba(2,6,23,0.10)", background: "#FFFFFF", boxShadow: "0 12px 28px rgba(2,6,23,0.05)", padding: 14 }, packageMeterCard: { borderRadius: 16, background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.08)", padding: 12, marginBottom: 12 }, packageMeterTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }, packageMeterTitle: { color: "#0F172A", fontWeight: 950, fontSize: 14 }, packageMeterSub: { color: "#64748B", fontWeight: 750, fontSize: 12, marginTop: 3 }, packagePercent: { color: "#047857", fontWeight: 950, fontSize: 16 }, packagePercentWarn: { color: "#B45309" }, packagePercentDanger: { color: "#B91C1C" }, packageTrack: { height: 9, borderRadius: 999, background: "#E2E8F0", overflow: "hidden", marginTop: 10 }, packageFill: { height: "100%", borderRadius: 999 }, packageFillOk: { background: "#10B981" }, packageFillWarn: { background: "#F59E0B" }, packageFillDanger: { background: "#EF4444" }, packageLimitText: { marginTop: 8, color: "#B91C1C", fontWeight: 850, fontSize: 12 }, packageHelpText: { marginTop: 8, color: "#64748B", fontWeight: 700, fontSize: 12 }, termsLabel: { display: "flex", alignItems: "flex-start", gap: 9, color: "#334155", fontWeight: 750, fontSize: 13, lineHeight: 1.4 }, termsCheckbox: { marginTop: 2 }, notice: { marginTop: 12, borderRadius: 14, padding: 12, fontWeight: 800, fontSize: 13, lineHeight: 1.4 }, noticeOk: { background: "#ECFDF5", color: "#065F46", border: "1px solid rgba(5,150,105,0.22)" }, noticeError: { background: "#FEF2F2", color: "#991B1B", border: "1px solid rgba(220,38,38,0.20)" }, progressWrap: { marginTop: 12 }, progressTrack: { height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }, progressFill: { height: "100%", borderRadius: 999, background: "#2563EB" }, progressText: { marginTop: 7, color: "#64748B", fontWeight: 800, fontSize: 12 }, analyzeButton: { width: "100%", marginTop: 13, border: "none", borderRadius: 16, padding: "15px 16px", background: "linear-gradient(135deg,#2563EB,#1D4ED8)", color: "#FFFFFF", fontWeight: 950, fontSize: 15, cursor: "pointer", boxShadow: "0 12px 24px rgba(37,99,235,0.25)", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }, buttonSpark: { marginRight: 0 }, buttonSpinner: { width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.50)", borderTopColor: "#FFFFFF", display: "inline-block", animation: "caseAnalyzerSpin 0.75s linear infinite", flex: "0 0 auto" }, buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },

  uploadModalOverlay: { position: "fixed", inset: 0, zIndex: 10000, display: "grid", placeItems: "center", padding: 18, background: "rgba(2,6,23,0.72)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", overflowY: "auto" },
  uploadModalCard: { position: "relative", width: "min(100%, 520px)", minWidth: 0, overflow: "hidden", borderRadius: 28, border: "1px solid rgba(255,255,255,0.70)", background: "linear-gradient(180deg,#FFFFFF 0%,#F8FAFF 100%)", boxShadow: "0 34px 90px rgba(2,6,23,0.42)", padding: "34px 32px 30px", textAlign: "center" },
  uploadModalCardMobile: { borderRadius: 22, padding: "28px 20px 24px" },
  uploadModalGlow: { position: "absolute", width: 220, height: 220, borderRadius: "50%", top: -145, left: "50%", transform: "translateX(-50%)", background: "rgba(37,99,235,0.22)", filter: "blur(20px)", pointerEvents: "none" },
  uploadModalIconWrap: { position: "relative", width: 66, height: 66, margin: "0 auto 18px", borderRadius: 22, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#2563EB,#1D4ED8)", boxShadow: "0 16px 32px rgba(37,99,235,0.28)" },
  uploadModalSpinner: { width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.38)", borderTopColor: "#FFFFFF", display: "block", animation: "caseAnalyzerSpin 0.75s linear infinite" },
  uploadModalTitle: { position: "relative", color: "#0F172A", fontWeight: 950, fontSize: 22, letterSpacing: "-0.02em" },
  uploadModalText: { position: "relative", margin: "10px auto 0", maxWidth: 430, color: "#475569", fontWeight: 700, fontSize: 14, lineHeight: 1.65 },
  uploadModalStatus: { marginTop: 20, borderRadius: 15, padding: "12px 14px", background: "#EFF6FF", border: "1px solid rgba(37,99,235,0.15)", color: "#1E40AF", fontWeight: 900, fontSize: 13, lineHeight: 1.4 },
  uploadModalFileCard: { marginTop: 13, borderRadius: 15, padding: "12px 13px", background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.09)", boxShadow: "0 8px 20px rgba(15,23,42,0.05)", textAlign: "left", minWidth: 0 },
  uploadModalFileTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, color: "#1D4ED8", fontSize: 12, fontWeight: 950 },
  uploadModalFileName: { marginTop: 7, color: "#0F172A", fontSize: 13, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  uploadModalStats: { marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px 12px", color: "#64748B", fontSize: 11, fontWeight: 800 },
  uploadModalTrack: { height: 9, marginTop: 16, borderRadius: 999, overflow: "hidden", background: "#E2E8F0" },
  uploadModalFill: { width: "45%", height: "100%", borderRadius: 999, background: "linear-gradient(90deg,#2563EB,#60A5FA,#2563EB)", backgroundSize: "200% 100%", animation: "caseAnalyzerUploadMove 1.4s linear infinite" },
  uploadModalFillDeterminate: { background: "linear-gradient(90deg,#2563EB,#60A5FA)", backgroundSize: "100% 100%", animation: "none", transition: "width 160ms ease" },
  uploadModalFoot: { marginTop: 13, color: "#64748B", fontWeight: 700, fontSize: 11, lineHeight: 1.45 },
  sidePanel: { borderRadius: 20, border: "1px solid rgba(15,23,42,0.08)", background: "#FFFFFF", boxShadow: "0 14px 34px rgba(15,23,42,0.07)", padding: 14 }, sideTitle: { color: "#0F172A", fontWeight: 950, fontSize: 15, marginBottom: 12 }, sideCopy: { margin: 0, color: "#64748B", fontWeight: 700, fontSize: 13, lineHeight: 1.5 }, sideButton: { marginTop: 12, appearance: "none", border: "1px solid rgba(37,99,235,0.55)", background: "#FFFFFF", color: "#2563EB", borderRadius: 12, padding: "10px 12px", width: "100%", cursor: "pointer", fontWeight: 950, fontSize: 13.5 }, miniStep: { display: "flex", gap: 10 }, miniStepLeft: { display: "flex", flexDirection: "column", alignItems: "center" }, miniStepNumber: { width: 26, height: 26, borderRadius: 10, background: "#EFF6FF", color: "#2563EB", display: "grid", placeItems: "center", fontWeight: 950, fontSize: 12 }, miniStepLine: { width: 2, flex: 1, minHeight: 22, background: "#E2E8F0" }, miniStepBody: { paddingBottom: 12 }, miniStepTitle: { color: "#0F172A", fontWeight: 950, fontSize: 13 }, miniStepText: { color: "#64748B", fontWeight: 650, fontSize: 12, lineHeight: 1.4, marginTop: 2 }, resultPanel: { marginTop: 16 },
};
